import { LoadingButton, TabPanel } from "@mui/lab";
import { styled } from "@mui/material/styles";
import { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  debounce,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Slide,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { CopyToClipboard } from "react-copy-to-clipboard";
import moment, { Moment } from "moment";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  AdminIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ChartIcon,
  ChartUpIcon,
  CheckIcon,
  ChipIcon,
  CloseIconCircle,
  CopyIcon,
  CrossIcon,
  Delete3Icon,
  DeleteIcon,
  DepositIcon,
  DownloadIcon,
  DraftIcon,
  Edit2Icon,
  Edit3Icon,
  InfoIcon,
  LocationIcon,
  MultipleIcon,
  PauseIcon,
  PlusIcon,
  PlusSignIcon,
  ReviewWarningIcon,
  SquareIcon,
  SquareMinusIcon,
  UserIcon,
  UsersAddIcon,
  WithdrawIcon,
} from "../assets/icons";
import { MainSpinner, TextSearch } from "../components";
import { RadioBoxCustom } from "../components/RadioBox";
import { Toggle } from "../components/Switch";
import useValidation from "../components/Validation";
import Api from "../openapi";
import {
  PartnerUserDetailResponse,
  PartnerUserDetailResponseDietTypeEnum,
  PartnerUserDetailResponseGenderEnum,
  UserMenuStatusResponse,
  MealBriefResponse,
  MealTypeResponse,
  CreateUserMenuSelectionByAdminRequestMealTypeEnum,
  AdminSubscriptionDetailResponse,
  SubscriptionActionResponse,
  AccountTransactionResponsePaymentTypeEnum,
  AccountTransactionResponseTransactionTypeEnum,
  AdminAddressRequest,
  DeliveryAddressCalendarResponse,
  DeliveryAddressCalendarResponseStatusEnum,
  DriverBriefResponse,
  AdminAddressResponse,
  SubscriptionDetailResponseStatusEnum,
  DietaryTargetsRequest,
  AdminUserMenuResponse,
  AMMLMealPortionResponse,
  AMMLMealIngredientResponse,
  AdminUserMenuSelectionResponse,
  AUMSCustomPortionRequestResponseStatusEnum,
  AUMSCustomPortionRequestResponse,
  AUMSSelectionPortionResponseTypeEnum,
  AUMSMealPortionResponse,
  AUMSSelectionPortionResponse,
  AdminUserMenuSelectionResponseMealTypeEnum,
  AreaBriefResponse,
  CoordinateResponse,
  AdminSubscriptionDetailResponseBalanceTypeEnum,
} from "../openapi/client";
import { setNotification } from "../store/notificationSlice";
import {
  activityLevelOptions,
  activityLevels,
  ChannelIcon,
  checkEmpty,
  checkIf,
  defaultFormat,
  dietGoals,
  disableDayCheck,
  displayFormat,
  generateRandom,
  getInitials,
  googleMapsApiKey,
  mergeByKey,
  PaymentType,
  TransactionType,
  formatCurrency,
  translateError,
} from "../utils";
import useAddressValidation from "../components/Validation/multipleAddress";
import useConfirm from "../hooks/useConfirm";
import { DeleteLabelIcon, PortionLabelIcon, SendLabelIcon } from "../components/IconLabel";
import { calculateDateFrame } from "./MealPlanning";
import { ArrowDropDown } from "@mui/icons-material";
import useMealTypes from "../hooks/useMealTypes";
import useZones from "../hooks/useZones";
import useMedicalConditions from "../hooks/useMedicalConditions";
import useAllergens from "../hooks/useAllergens";
import useDislikes from "../hooks/useDislikes";
import useDrivers from "../hooks/useDrivers";
import SubscriptionCrud from "../components/SubscriptionCrud";
import { useMeals } from "../hooks";
import { TabPanelProps } from "../openapi/interfaces";
import theme from "../theme";
import { v4 as uuid } from "uuid";
import useAffiliates from "../hooks/useAffiliates";
import Transition from "../components/Transition";
import { saveAs } from "file-saver";
import { DateCalendar, DatePicker, DateValidationError } from "@mui/x-date-pickers";
import CustomDayDelivery from "../components/Calendar/planDeliveryDay";
import CustomDaySubscription from "../components/Calendar/planSubscriptionDay";
import DataTable from "../components/DataTable";
import usePaymentHistory from "../hooks/usePaymentHistory";
import { GridCallbackDetails, GridPaginationModel, GridRenderCellParams } from "@mui/x-data-grid";
import TransactionCrud from "../components/TransactionCrud";
import useUserSubscriptions from "../hooks/useUserSubscriptions";
import useDietitians from "../hooks/useDietitians";
import useAddress from "../hooks/useAddress";
import useSubscriptionValidation from "../components/SubscriptionCrud/SubscriptionValidation";
import useLockedDays from "../hooks/useLockedDays";
import Can from "../components/Can";
import DietaryTargets from "../components/UserCrud/targetCalories";
import DeliveryDays from "../components/UserCrud/deliveryDays";
import PortionCustomize from "../components/PortionCustomize";
import { useDebounce } from "../hooks/useDebounce";
import useAreasBrief from "../hooks/useAreaBrief";
import AddressMap from "../components/UserCrud/map";
import { APIProvider } from "@vis.gl/react-google-maps";
import useGetUsersOfMembership from "../hooks/useGetUsersOfMembership";
import UserCrud from "../components/UserCrud";

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "white",
    color: theme.palette.text.dark,
    fontSize: 12,
    fontWeight: 500,
    padding: "12px",
    boxShadow: "0 4px 12px 0 rgba(0, 0, 0, 0.12)",
    borderRadius: "12px",
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: "white",
  },
}));

interface IProps {
  activeTab?: number;
  userId?: string;
  date?: string;
}

interface CustomState {
  [key: string]: any;
}
interface ISelection {
  selectionId: string;
  mealId?: string;
  portionType?: AUMSSelectionPortionResponseTypeEnum;
  selectedPortion?: number;
  dietitianNote?: string;
  removedIngredients?: string[];
  updated?: boolean;
  added?: boolean;
  process?: boolean;
}

interface ITargetCalorie {
  value?: number;
  updated?: boolean;
  process?: boolean;
  editMode?: boolean;
}

interface CalendarError {
  startDate?: DateValidationError;
  endDate?: DateValidationError;
}

export interface DateSelection {
  id?: string;
  disabled?: boolean;
  startDate: any;
  endDate: any;
}

export interface IMealIngredientResponse extends AMMLMealIngredientResponse {
  selected?: boolean;
}

export interface IAddressResponse extends AdminAddressResponse {
  dirty?: boolean;
  driverId?: string;
  area?: string;
}

export interface IAddressRequest extends AdminAddressRequest {
  driver?: DriverBriefResponse;
  driverId?: string;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && (
        <Box>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

export default function UserDetail(props: IProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const findActiveTab = checkIf(searchParams.get("activeTab"))
    ? Number(searchParams.get("activeTab"))
    : checkIf(props.activeTab)
    ? props.activeTab
    : 0;
  let { id } = useParams();
  if (checkIf(props.userId)) {
    id = props.userId;
  }
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const zones = useZones();
  const areas = useAreasBrief();
  const { lockedStart: lockedStartGlobal, lockedDays: lockedDaysNumber } = useLockedDays();
  const [userReference, setUser] = useState<PartnerUserDetailResponse>();
  const [user, setUserEdit] = useState<any>();
  const [bmrValues, setBmrValues] = useState<any>();
  const userDebounce = useDebounce(bmrValues, 1000);
  const { validation, setValidation } = useValidation(user as PartnerUserDetailResponse);
  const [activeTab, setActiveTab] = useState(findActiveTab);
  const [userDetailActiveTab, setUserDetailActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveAddressLoading, setSaveAddressLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [customAllergens, setCustomAllergens] = useState<string[]>([]);
  const [customAllergen, setCustomAllergen] = useState("");
  const [list, setList] = useState<Array<string>>();
  const allergenList = useAllergens();
  const medicalConditionList = useMedicalConditions();
  const dislikeList = useDislikes();
  const [hideMealTypes, setHideMealTypes] = useState(true);
  const [activeSubscription, setActiveSubscription] = useState<AdminSubscriptionDetailResponse>();

  const [startDate, setStartDate] = useState<Moment | null>(user?.birthDate ? moment(user.birthDate) : null);
  const [open, setOpen] = useState(false);
  const calendarRef = useRef<any>(null);
  const [dateSelectionError, setDateSelectionError] = useState<DateValidationError | null>(null);
  const errorMessage = useMemo(() => {
    switch (dateSelectionError) {
      case "shouldDisableDate": {
        return "Your age is required to be between 9 and 99";
      }
      case "invalidDate": {
        return "Your date is not valid";
      }
      default: {
        return "";
      }
    }
  }, [dateSelectionError]);

  const [addressValidationTarget, setAddressValidationTarget] = useState<IAddressResponse>();
  const { addressList, addressListRef, setAddressList, updateAddressList, createAddressItem, deleteAddressItem } =
    useAddress(user?.id);
  const { validation: addressValidation, setValidation: setAddressValidation } = useAddressValidation(
    addressValidationTarget as IAddressResponse
  );
  const [addressActiveTab, setAddressActiveTab] = useState<string>();
  const [retriggerDeliveryCalendar, setRetriggerDeliveryCalendar] = useState<number>();
  const [calendarDeliveryDate, setCalendarDeliveryDate] = useState<Moment | null>(moment());
  const [deliveryInfo, setDeliveryInfo] = useState<DeliveryAddressCalendarResponse[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<Moment>(moment().startOf("month"));
  const [deliveryCalendarDisabled, setDeliveryCalendarDisabled] = useState(false);

  const [value, setValue] = useState<CustomState>({});
  const [medicalConditionSelections, setMedicalConditionSelections] = useState<CustomState>({});
  const [dislikeSelections, setDislikeSelections] = useState<CustomState>({});
  const dietitianList = useDietitians();
  const driverList = useDrivers();
  const affiliateList = useAffiliates();
  const [formDirty, setFormDirty] = useState(false);
  const [copied, setCopied] = useState(false);
  const [retriggerReq, setRetriggerReq] = useState<number>();
  const [invoiceDownloadProcess, setInvoiceDownloadProcess] = useState(false);
  const [enablePhoneNumberChange, setEnablePhoneNumberChange] = useState(false);

  // subscriptions
  const [retriggerSubscriptionReq, setRetriggerSubscriptionReq] = useState<number>();
  const [targetCalorie, setTargetCalorie] = useState<ITargetCalorie>();
  const userSubscriptions = useUserSubscriptions(userReference?.id, retriggerSubscriptionReq);

  // multiple user dialog
  const [multipleUserDialog, setMultipleUserDialog] = useState<{
    open: boolean;
  }>({
    open: false,
  });
  const [retriggerMultipleUserReq, setRetriggerMultipleUserReq] = useState<number>();
  const { users: membershipUsers } = useGetUsersOfMembership(user?.membershipId, retriggerMultipleUserReq);

  const [ConfirmDialog, confirmDelete] = useConfirm({
    titleIcon: <DeleteLabelIcon />,
    text: "You are about to permanently delete this user, and this action is irreversible. Are you sure you want to proceed?",
  });
  const [PortionChangeDialog, confirmPortionChange] = useConfirm({
    titleIcon: <PortionLabelIcon />,
    text: "The selected total calories are more than the target calories. Do you still want to continue ?",
  });
  const [ConfirmSendPasswordDialog, confirmSendPassword] = useConfirm({
    titleIcon: <SendLabelIcon />,
    text: "You are about to reset user's password and send it via SMS. Are you sure you want to proceed?",
  });
  const [SubscriptionCancelDialog, confirmSubscriptionCancel] = useConfirm({
    titleIcon: <DeleteLabelIcon />,
    text: "You are about to cancel the subscription permanently, this action is irreversible. Are you sure you want to proceed?",
  });
  const [ActualSubscriptionCancelDialog, confirmActualSubscriptionCancel] = useConfirm({
    titleIcon: <DeleteLabelIcon />,
    text: "You are about to cancel a subscription paid by credit card through Rejeem. The amount will be transferred to the user's wallet and a fee will be applied to your next invoice. This action is irreversible. Are you sure you want to proceed?",
  });
  const [AddressTabChangeDialog, confirmAddressChange] = useConfirm({
    titleIcon: <SendLabelIcon />,
    text: "There are unsaved changes in the address details. Please save or discard your changes.",
    yes: "Save",
    no: "Discard",
    permission: "USER_ADDRESS_EDIT",
  });
  const [ConfirmDeleteAddressDialog, confirmDeleteAddress] = useConfirm({
    titleIcon: <DeleteLabelIcon />,
    text: "You are about to permanently delete this address, and this action is irreversible. Are you sure you want to proceed?",
  });

  // dietary targets
  useEffect(() => {
    if (checkIf(userSubscriptions)) {
      const activeSubscription = userSubscriptions.find(
        (subs) => subs.active && subs.status === SubscriptionDetailResponseStatusEnum.Active
      );
      setActiveSubscription(activeSubscription);
    }
  }, [userSubscriptions]);

  const handleDietaryTargetChange = (field: any, value?: any) => {
    setFormDirty(true);
    setActiveSubscription((state: any) => ({ ...state, dietaryTargets: { ...state.dietaryTargets, [field]: value } }));
  };

  const { validation: subscriptionValidation, setValidation: setSubscriptionValidation } = useSubscriptionValidation(
    activeSubscription as AdminSubscriptionDetailResponse
  );

  // console.log("subscriptionValidation", subscriptionValidation);
  // console.log("dietaryTargets", dietaryTargets);
  // console.log("userSubscriptions", userSubscriptions);

  // Payment History
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  const handlePaginationChange = (model: GridPaginationModel, details: GridCallbackDetails<any>) => {
    setPaginationModel(model);
  };

  // Address delivery selections

  const back = () => navigate("/users");

  useEffect(() => {
    if (checkIf(addressList)) {
      setAddressValidationTarget({ ...addressList.find((i) => i.id === addressActiveTab) });
    }
  }, [addressList]);

  useEffect(() => {
    if (checkIf(addressListRef)) {
      const activeTab =
        checkIf(addressActiveTab) && addressListRef?.find((i) => i.id === addressActiveTab)
          ? addressActiveTab
          : addressListRef![0].id;
      setAddressValidationTarget({ ...addressList.find((i) => i.id === activeTab) });

      setAddressActiveTab((state) => {
        return checkIf(state) && addressListRef?.find((i) => i.id === state) ? state : addressListRef![0].id;
      });
    }
  }, [addressListRef]);

  useEffect(() => {
    const getUser = async () => {
      try {
        setLoading(true);
        const res = await Api.User.getUserById(String(id));
        setUser(res.data);
        setUserEdit(res.data);
        setStartDate(res.data.birthDate ? moment(res.data.birthDate) : null);
        setValue(() => {
          let temp: CustomState = {};
          Array.from(new Set(res.data.allergens)).forEach((allergen: any) => {
            temp[allergen] = true;
          });
          return temp;
        });
        setMedicalConditionSelections(() => {
          let temp: CustomState = {};
          Array.from(new Set(res.data.medicalConditions)).forEach((medicalCondition: any) => {
            temp[medicalCondition] = true;
          });
          return temp;
        });
        setDislikeSelections(() => {
          let temp: CustomState = {};
          Array.from(new Set(res.data.dislikes)).forEach((dislike: any) => {
            temp[dislike] = true;
          });
          return temp;
        });
      } catch (error) {}
      setLoading(false);
    };
    getUser();
  }, [id, retriggerReq]);

  const getDeliveryCalendar = async (startDate: string, endDate: string) => {
    try {
      const res = await Api.Address.getAddressesCalendarByAdmin(user.id, startDate, endDate);
      setDeliveryInfo(res.data);
    } catch (error) {}
  };

  // console.log("user userDetail", user);
  // console.log("validation userDetail", validation);
  // console.log("active", activeSubscription);

  useEffect(() => {
    const start = moment(selectedMonth).startOf("month").format(defaultFormat);
    const end = moment(selectedMonth).endOf("month").format(defaultFormat);
    checkIf(addressActiveTab) && getDeliveryCalendar(start, end);

    if (checkIf(addressActiveTab)) {
      // map related
      setSelectedAddress(addressActiveTab);
      const selectedAddress = addressList.find((i) => i.id === addressActiveTab);
      const { lat, lon } = (selectedAddress?.location as CoordinateResponse) ?? { lat: undefined, lon: undefined };
      setSelectedCoordinates({ lat: Number(lat), lng: Number(lon), detail: String(selectedAddress?.detail) });
    }
  }, [addressActiveTab, retriggerDeliveryCalendar]);

  const saveChanges = async () => {
    if (validation.error) {
      setValidation((state) => ({ ...state, show: true }));
      return;
    }

    if (subscriptionValidation.error) {
      setSubscriptionValidation((state) => ({ ...state, show: true }));
      return;
    }

    const updateUserDetail: any = {
      fullName: user.fullName,
      gender: user.gender,
      birthDate: checkIf(user.birthDate) ? moment(user.birthDate).format(defaultFormat) : null,
      height: checkIf(user.height) ? Number(user.height) : null,
      weight: checkIf(user.weight) ? Number(user.weight) : null,
      targetWeight: checkIf(user.targetWeight) ? Number(user.targetWeight) : null,
      allergens: Object.keys(value).filter((v) => Boolean(value[v])),
      medicalConditions: Object.keys(medicalConditionSelections).filter((v) => Boolean(medicalConditionSelections[v])),
      dislikes: Object.keys(dislikeSelections).filter((v) => Boolean(dislikeSelections[v])),
      externalId: user.externalId || "",
      phoneNumber: user.phoneNumber,
      deliveryPhoneNumber: user.deliveryPhoneNumber?.length !== 4 ? user.deliveryPhoneNumber : undefined,
      active: user.active,
      dietitianId: user.dietitianId,
      driverId: user.driverId,
      affiliateId: user.affiliateId,
      dietType: user.dietType,
      dietGoal: user.dietGoal,
      activityLevel: user.activityLevel,
      deliveryNote: user.deliveryNote,
    };

    try {
      setSaveLoading(true);
      const res = await Api.User.updateUserByAdmin(user.id, updateUserDetail);
      if (checkIf(activeSubscription?.id)) {
        const dietaryUpdate = await Api.Subscription.updateDietaryTargetsByAdmin(
          String(activeSubscription?.id),
          activeSubscription?.dietaryTargets as DietaryTargetsRequest
        );
      }

      dispatch(
        setNotification({
          show: true,
          severity: "success",
          message: "Profile info has been updated.",
        })
      );
      setUser(user);
      setEnablePhoneNumberChange(false);
      setRetriggerSubscriptionReq(generateRandom());
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    }
    setSaveLoading(false);
    setFormDirty(false);
  };

  const saveAddressChange = async () => {
    if (addressValidation.error) {
      setAddressValidation((state) => ({ ...state, show: true }));
      return;
    }
    const { dirty, driver, ...payload } = addressList.find((i) => i.id === addressActiveTab) as IAddressResponse;
    const transaction = {
      ...payload,
    };
    if (!checkEmpty(payload.building)) {
      transaction.building = Number(payload.building);
    }
    if (!checkEmpty(payload.flat)) {
      transaction.flat = Number(payload.flat);
    }
    if (!checkEmpty(payload.street)) {
      transaction.street = Number(payload.street);
    }
    if (!checkEmpty(payload.zone)) {
      transaction.zone = Number(payload.zone);
    }
    if (!checkEmpty(payload.driverId)) {
      transaction.driverId = payload.driverId;
    } else {
      transaction.driverId = driver?.id;
    }
    if (!checkEmpty(payload.area)) {
      transaction.area = payload.area;
    }

    setSaveAddressLoading(true);

    // check if new entry
    const find = addressListRef?.find((i) => i.id === addressActiveTab);
    if (!find) {
      try {
        const data = await createAddressItem(transaction as AdminAddressRequest);
        setAddressActiveTab(data?.id);
        setDeliveryCalendarDisabled(false);
      } catch (error) {}
    } else {
      // update the record
      try {
        await updateAddressList(String(payload.id), transaction as AdminAddressRequest);
      } catch (error) {}
    }
    setRetriggerDeliveryCalendar(generateRandom());
    setAddressValidation((state) => ({ ...state, show: false }));
    setSaveAddressLoading(false);
  };

  const deleteAddress = async () => {
    const answer = await confirmDeleteAddress();
    if (!answer) return;

    try {
      setSaveAddressLoading(true);
      await deleteAddressItem(String(addressActiveTab));
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    } finally {
      setSaveAddressLoading(false);
    }
  };

  const discardAddressChange = () => {
    setDeliveryCalendarDisabled(false);
    setAddressList((state) => {
      let temp = [...state];

      // get the reference item back
      const orgItem = addressListRef?.find((i) => i.id === addressActiveTab);

      // if the original item is in the ref
      if (orgItem) {
        const index = temp.findIndex((i) => i.id === orgItem.id);
        temp[index] = { ...orgItem };
      } else {
        // remove it from the addressList
        const index = temp.findIndex((i) => i.id === addressActiveTab);
        temp.splice(index, 1);
      }
      return temp;
    });
    setAddressActiveTab((state) => {
      const find = addressListRef?.find((i) => i.id === state);
      if (find) {
        return state;
      } else {
        return addressListRef?.[0].id;
      }
    });
    setAddressValidation((state) => ({ ...state, show: false }));
  };

  // console.log("address validation", addressValidation);
  // console.log("addressList", addressList);
  // console.log("allergenList", allergenList);

  const addressTabChange = async (targetTabId?: string) => {
    setDeliveryCalendarDisabled(false);
    // if the address is dirty, prompt the message
    const found = addressList.find((i) => i.id === addressActiveTab);
    const dirty = found?.dirty;

    if (dirty) {
      const answer = await confirmAddressChange();
      if (answer) {
        // check if any validation error
        if (addressValidation.error) {
          setAddressValidation((state) => ({ ...state, show: true }));
          return;
        }
        await saveAddressChange();
      } else {
        discardAddressChange();
      }
    }

    if (checkIf(targetTabId)) {
      setAddressActiveTab(targetTabId);
    } else {
      createNewAdress();
      setDeliveryCalendarDisabled(true);
    }
  };

  const calculateBMRandRC = async () => {
    try {
      const res = await Api.HealthMetrics.calculateHealthMetrics({
        gender: user.gender,
        birthDate: moment(user.birthDate).format(defaultFormat),
        height: user.height,
        weight: user.weight,
        dietGoal: user.dietGoal,
        activityLevel: user.activityLevel,
      });
      setUserEdit((state: any) => ({
        ...state,
        healthMetrics: {
          ...state.healthMetrics,
          bodyMassIndex: res.data.bodyMassIndex,
          basalMetabolicRate: res.data.basalMetabolicRate,
          recommendedCalories: res.data.recommendedCalories,
        },
      }));
    } catch (error) {}
  };

  useEffect(() => {
    calculateBMRandRC();
  }, [userDebounce]);

  const handleFieldChange = (field: string, value: any) => {
    const fields = ["gender", "birthDate", "height", "weight", "dietGoal", "activityLevel"];

    if (fields.includes(field)) {
      setBmrValues((state: any) => ({ ...state, [field]: value }));
    }

    setFormDirty(true);
    setUserEdit((state: any) => ({ ...state, [field]: value }));
  };

  const handleChangeBirthdate = (newValue: Moment | null) => {
    setStartDate(newValue as Moment);
    handleFieldChange("birthDate", moment(newValue).format(defaultFormat));
  };

  const createNewAdress = () => {
    if (addressList.length === 5) return;
    const uniqueId = uuid();
    let payload: IAddressResponse = {
      id: uniqueId,
      name: "New Address",
      default: false,
      building: undefined,
      street: undefined,
      flat: undefined,
      detail: undefined,
      dirty: true,
      zone: undefined,
    };
    setAddressList((state) => {
      let temp = [...state];
      temp.push(payload);
      return temp;
    });
    setAddressActiveTab(uniqueId);
  };

  const handleDeliverySelection = async (value: Moment | null) => {
    try {
      const date = moment(value).format(defaultFormat);
      const res = await Api.Address.updateAddressesCalendarByAdmin(user.id, { date, addressId: addressActiveTab });
      // update delivery info accordingly
      setDeliveryInfo((state) => {
        let temp = [...state];
        const findIndex = temp.findIndex((i) => i.date === date);
        if (findIndex > -1) {
          temp[findIndex].status = DeliveryAddressCalendarResponseStatusEnum.Selected;
          temp[findIndex].addressId = addressActiveTab;
        }
        return temp;
      });
    } catch (error) {}
  };

  const handleMonthChange = (month: Moment) => {
    setSelectedMonth(month);
    const start = moment(month).startOf("month").format(defaultFormat);
    const end = moment(month).endOf("month").format(defaultFormat);
    getDeliveryCalendar(start, end);
  };

  const checkDirty = useCallback(
    (input: any) => {
      let allowedNumber = ["street", "building", "flat", "detail"];
      const ref = addressListRef?.find((i) => i.id === input.id) as IAddressResponse;
      if (!ref) return true;
      return !Object.keys(ref).every((i) => {
        if (allowedNumber.includes(i) && checkEmpty(input[i])) {
          return Boolean(ref[i as keyof IAddressResponse]) === Boolean(input[i]);
        } else {
          return ref[i as keyof IAddressResponse] == input[i];
        }
      });
    },
    [addressListRef]
  );

  const handleAddressChange = useCallback(
    (addressId: string, field: string, value: any) => {
      setAddressList((state) => {
        let temp = [...state];
        const findIndex = temp.findIndex((i) => i.id === addressId);
        if (field === "name") {
          value = value.toString().slice(0, 20);
        }
        temp[findIndex][field as keyof IAddressResponse] = value;
        temp[findIndex].dirty = checkDirty(temp[findIndex]);
        return temp;
      });
    },
    [addressListRef]
  );

  const handleChange = (event: any) => {
    setFormDirty(true);
    setValue((state) => ({
      ...state,
      [event.target.name]: event.target.checked,
    }));
  };

  const handleMedicalConditionChange = (event: any) => {
    setFormDirty(true);
    setMedicalConditionSelections((state) => ({
      ...state,
      [event.target.name]: event.target.checked,
    }));
  };

  const handleDislikeChange = (event: any) => {
    setFormDirty(true);
    setDislikeSelections((state) => ({
      ...state,
      [event.target.name]: event.target.checked,
    }));
  };

  const dietitianChange = (event: SelectChangeEvent) => handleFieldChange("dietitianId", event.target.value);

  const addCustomAllergen = () => {
    // find custom is present in the system allergens list
    const findIndex = allergenList.findIndex((i) => i.toLowerCase() === customAllergen.toLowerCase().trim());

    if (findIndex > -1) {
      setValue((state) => ({
        ...state,
        [allergenList[findIndex]]: true,
      }));
      setCustomAllergen("");
      setFormDirty(true);
      return;
    }

    setCustomAllergens((state) => {
      if (!state.find((i) => i.toLowerCase() === customAllergen.toLowerCase().trim())) {
        return [...state, customAllergen.trim()];
      }
      return state;
    });

    setValue((state) => {
      const temp = { ...state };
      const objKeys = Object.keys(temp);
      const findIndex = objKeys.findIndex((i) => i.toLowerCase() === customAllergen.toLowerCase().trim());
      if (findIndex > -1) {
        temp[objKeys[findIndex]] = true;
      } else {
        temp[customAllergen.trim()] = true;
      }

      return temp;
    });

    setCustomAllergen("");
    setFormDirty(true);
  };

  useEffect(() => {
    if (checkIf(user) && checkIf(allergenList)) {
      const customAllergens = user.allergens?.filter((i: string) => !allergenList.includes(i));
      setCustomAllergens(customAllergens as string[]);
    }
  }, [allergenList, user]);

  // console.log("customAllergens", customAllergens);
  // console.log("allergenList", value);

  const deleteCustomAllergen = (value: string) => {
    setCustomAllergens((state) => {
      const temp = [...state];
      const index = temp.indexOf(value);
      temp.splice(index, 1);
      return temp;
    });

    setValue((state) => {
      const temp = { ...state };
      const objKeys = Object.keys(temp);
      const findIndex = objKeys.findIndex((i) => i.toLowerCase() === value.toLowerCase().trim());
      if (findIndex > -1) {
        temp[objKeys[findIndex]] = false;
      }
      return temp;
    });
    setFormDirty(true);
  };

  const deleteUser = async () => {
    const answer = await confirmDelete();
    if (!answer) return;
    return;
  };

  const activity = useMemo(() => {
    const userActive = Boolean(user?.active);
    return {
      class: userActive ? "active" : "passive",
      value: userActive ? "Active" : "Inactive",
    };
  }, [user]);

  const planInfo = useMemo(() => {
    const currentPlans = userSubscriptions?.filter((subscription) => subscription.status === "ACTIVE");
    const pastPlans = userSubscriptions?.filter((subscription) => subscription.status === "PAST");
    const futurePlans = userSubscriptions?.filter((subscription) => subscription.status === "FUTURE");
    const cancelledPlans = userSubscriptions?.filter((subscription) => subscription.status === "CANCELLED");

    // setTargetCalorie(() => {
    //   const checkValue = userSubscriptions?.find((i) => Boolean(i.active))?.targetCalories;
    //   const value = checkIf(checkValue) ? Number(checkValue) : 0;
    //   return {
    //     value,
    //     updated: false,
    //     process: false,
    //     editMode: false,
    //   };
    // });

    return {
      currentPlans,
      futurePlans,
      pastPlans,
      cancelledPlans,
    };
  }, [userSubscriptions]);

  // MEAL PLAN SECTION
  const meals = useMeals();
  const mealTypes = useMealTypes();
  const [selectableDates, setSelectableDates] = useState<UserMenuStatusResponse[]>([]);
  const findSelectedDate = checkIf(searchParams.get("date"))
    ? (searchParams.get("date") as string)
    : checkIf(props.date)
    ? (props.date as string)
    : moment().format(defaultFormat);
  const [selectedDate, setSelectedDate] = useState<string>(findSelectedDate);
  const [currentDateFrame, setCurrentDateFrame] = useState<Moment[]>(
    calculateDateFrame(searchParams.get("date") ? moment(searchParams.get("date")) : moment())
  );
  const [userSelections, setUserSelections] = useState<AdminUserMenuSelectionResponse[]>([]);
  const [userMenu, setUserMenu] = useState<AdminUserMenuResponse>({});
  const [updateStatusLoading, setUpdateStatusLoading] = useState(false);
  const [globalLoading, setGlobalLoading] = useState(false);
  const [mealTypePanelState, setMealTypePanelState] = useState<any>({});
  const [selectionCache, setSelectionCache] = useState<Array<ISelection>>([]);
  const inputRef = useRef<HTMLInputElement>();
  const phoneNumberRef = useRef<HTMLInputElement>();
  const [subscriptionCrudOpen, setSubscriptionCrudOpen] = useState(false);
  const [subscriptionEditOpen, setSubscriptionEditOpen] = useState(false);
  const [planToEdit, setPlanToEdit] = useState<AdminSubscriptionDetailResponse>();
  const [retriggerMealPlanReq, setRetriggerMealPlanReq] = useState<number>();

  const getSelectableDays = async (startDate: string, endDate: string) => {
    try {
      const res = await Api.UserMenu.getMenuStatusesByDate(String(id), startDate, endDate);
      setSelectableDates((state) => {
        return mergeByKey(state, res.data, "date");
      });
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const currentDateFrameStart = moment(currentDateFrame[0]).startOf("month").format(defaultFormat);
    const currentDateFrameEnd = moment(currentDateFrame[6]).endOf("month").format(defaultFormat);
    const foundStart = selectableDates.find((item) => item.date === currentDateFrameStart);
    const foundEnd = selectableDates.find((item) => item.date === currentDateFrameEnd);
    if (!foundEnd || !foundStart || retriggerMealPlanReq) {
      getSelectableDays(currentDateFrameStart, currentDateFrameEnd);
    }
  }, [currentDateFrame, retriggerMealPlanReq]);

  useEffect(() => {
    const getSelectionsByDate = async () => {
      setUserMenu({});
      setUserSelections([]);
      setGlobalLoading(true);
      try {
        const res = await Api.UserMenu.getUserMenuByUserAndDate(String(id), selectedDate);
        setUserMenu(res.data);
        setUserSelections(res.data.selections || []);
        setSelectionCache(
          (res.data.selections || []).map((i) => {
            let selection: ISelection = {
              selectionId: String(i.id),
              selectedPortion: Number(i.portion?.portion),
              portionType: i.portion?.type,
              dietitianNote: i.dietitianNote,
              removedIngredients: Array.from(i.removedIngredients as Set<string>),
            };
            return selection;
          })
        );
      } catch (error) {
        console.log(error);
      }
      setGlobalLoading(false);
    };
    getSelectionsByDate();
  }, [selectedDate, retriggerMealPlanReq]);

  // console.log("userSelections", userSelections);
  // console.log("selectionCache", selectionCache);

  const calculation = useMemo(() => {
    let protein = 0;
    let carbs = 0;
    let fat = 0;
    let cal = 0;
    userSelections.map((selection) => {
      // const selectedPortion = selectionCache.find((i) => i.selectionId === selection.id)?.selectedPortion;
      // const targetPortion = selection.mealPortions?.find((i: AMMLMealPortionResponse) => i.portion === selectedPortion);
      const targetPortion = selection.portion;
      protein += Number(targetPortion?.protein);
      carbs += Number(targetPortion?.carbs);
      fat += Number(targetPortion?.fat);
      cal += Number(targetPortion?.calories);
    });
    return {
      protein: Math.round(protein * 100) / 100,
      carbs: Math.round(carbs * 100) / 100,
      fat: Math.round(fat * 100) / 100,
      cal: Math.round(cal * 100) / 100,
    };
  }, [selectionCache, userSelections]);

  const selectionMode = useMemo(() => {
    const status = selectableDates.find((i) => i.date === selectedDate)?.status;
    return {
      state: status !== "SELECTABLE",
      status,
    };
  }, [selectedDate, selectableDates, retriggerMealPlanReq]);

  const targetCalorieValueChange = (event: any) => {
    const value = event.target.value;
    const test = value.match(/[0-9]*/);
    if (test[0] !== "" || value.length === 0) {
      setTargetCalorie((state) => ({ ...state, value: Number(test[0]), updated: true, editMode: true }));
    }
  };

  const handleEditModeClick = () => {
    setTargetCalorie((state) => ({ ...state, editMode: true }));
    inputRef.current?.focus();
  };

  // const handleSaveModeClick = async () => {
  //   setTargetCalorie((state) => ({ ...state, editMode: false, process: true }));
  //   try {
  //     const res = await Api.Subscription.updateTargetCaloriesByAdmin(String(id), {
  //       targetCalories: targetCalorie?.value,
  //     });
  //     setTargetCalorie((state) => ({ ...state, process: false, updated: false }));
  //   } catch (error: any) {
  //     dispatch(
  //       setNotification({
  //         show: true,
  //         severity: "error",
  //         message: error.response?.data?.message || error.message,
  //       })
  //     );
  //     setTargetCalorie((state) => ({ ...state, process: false }));
  //     inputRef.current?.blur();
  //   }
  // };

  const handlePanelExpandChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setMealTypePanelState((state: any) => ({ ...state, [panel]: isExpanded }));
  };

  const updateChecker = (meal: AdminUserMenuSelectionResponse, stateItem: ISelection) => {
    // if the selection is newly added, return updated true
    if (stateItem?.added === false) return true;

    const ingredientUpdateFalseCheck =
      Array.from(meal.removedIngredients as Set<string>)?.every((i) => stateItem.removedIngredients?.includes(i)) &&
      Array.from(meal.removedIngredients as Set<string>)?.length === stateItem.removedIngredients?.length;
    const dietitianNoteUpdateFalseCheck =
      meal.dietitianNote?.toString().trim() === stateItem.dietitianNote?.toString().trim();
    const portionUpdateFalseCheck = Number(meal.portion) === Number(stateItem.selectedPortion);
    return !ingredientUpdateFalseCheck || !dietitianNoteUpdateFalseCheck || !portionUpdateFalseCheck;
  };

  const handlePortionChange = async (meal: AdminUserMenuSelectionResponse, event: SelectChangeEvent<any>) => {
    let cal = 0;
    userSelections.map((selection) => {
      let selectedPortion: number | undefined;
      if (selection.id === meal.id) {
        selectedPortion = Number(event.target.value);
      } else {
        selectedPortion = selectionCache.find((i) => i.selectionId === selection.id)?.selectedPortion;
      }
      let targetPortion = selection.meal?.portions?.find((i: AMMLMealPortionResponse) => i.portion === selectedPortion);
      if (!checkIf(targetPortion)) {
        targetPortion = { ...selection.portion };
      }
      cal += Number(targetPortion?.calories);
    });

    cal = Math.round(cal * 100) / 100;

    if (calculation.cal < Number(targetCalorie?.value) && cal > Number(targetCalorie?.value)) {
      const answer = await confirmPortionChange();
      if (!answer) return;
    }

    setSelectionCache((state) => {
      const cacheValue = event.target.value.split(" ");
      const selectedPortionValue = cacheValue[0];
      const selectedPortionType = cacheValue[1];
      let temp = [...state];
      const index = temp.findIndex((i) => i.selectionId === meal.id);
      temp[index].selectedPortion = Number(selectedPortionValue);
      temp[index].portionType = selectedPortionType;
      temp[index].updated = updateChecker(meal, temp[index]);
      return temp;
    });
  };

  const handleDietitianNoteChange = (meal: AdminUserMenuSelectionResponse, dietitianNote: string) => {
    setSelectionCache((state) => {
      let temp = [...state];
      const index = temp.findIndex((i) => i.selectionId === meal.id);
      if (index > -1) {
        temp[index].dietitianNote = dietitianNote;
      }
      temp[index].updated = updateChecker(meal, temp[index]);

      return temp;
    });
  };

  const onIngredientChange = (meal: AdminUserMenuSelectionResponse, ingredientId: string, checked: boolean) => {
    setSelectionCache((state) => {
      let temp = [...state];
      const index = temp.findIndex((i) => i.selectionId === meal.id);
      const ingredientIndex = temp[index].removedIngredients?.findIndex((i) => i === ingredientId);
      if (checkIf(ingredientIndex) && Number(ingredientIndex) > -1) {
        temp[index].removedIngredients?.splice(Number(ingredientIndex), 1);
      } else {
        temp[index].removedIngredients?.push(ingredientId);
      }

      temp[index].updated = updateChecker(meal, temp[index]);

      return temp;
    });
  };

  const mealSelection = async (event: MouseEvent, value: MealBriefResponse | null, mealType: MealTypeResponse) => {
    try {
      const res = await Api.AdminMeal.getMeal(String(value?.id));
      const meal = res.data;
      const uniqueId = uuid();
      const mealData: AdminUserMenuSelectionResponse = {};

      mealData.addedByDietitian = true;
      mealData.dietitianNote = "";
      mealData.id = uniqueId;
      mealData.mealType = String(mealType.name) as AdminUserMenuSelectionResponseMealTypeEnum;
      mealData.portion = {
        type: AUMSSelectionPortionResponseTypeEnum.Default,
        ...Array.from(meal.portions as AMMLMealPortionResponse[]).find((i) => i.default),
      };
      mealData.meal = { ...meal, name: meal.name?.en };
      mealData.selectorRole = "ADMIN";

      const selection: ISelection = {
        selectionId: uniqueId,
        mealId: meal.id,
        selectedPortion: mealData.portion.portion,
        portionType: AUMSSelectionPortionResponseTypeEnum.Default,
        dietitianNote: "",
        removedIngredients: [],
        updated: true,
        added: false,
      };

      setUserSelections((state) => [...state, mealData]);
      setSelectionCache((state) => [...state, selection]);
    } catch (error) {}
  };

  const handleSaveChanges = async (meal: AdminUserMenuSelectionResponse) => {
    const selection = selectionCache.find((i) => i.selectionId === meal.id);

    setSelectionCache((state) => {
      let temp = [...state];
      const index = temp.findIndex((i) => i.selectionId === meal.id);
      temp[index].process = true;
      return temp;
    });

    // if the selection is to be added as a new item on backend
    if (selection?.added === false) {
      try {
        const res = await Api.UserMenu.createSelectionByAdmin(String(id), {
          mealType: meal.mealType as CreateUserMenuSelectionByAdminRequestMealTypeEnum,
          mealId: String(selection.mealId),
          date: selectedDate,
          portion: selection.selectedPortion,
          dietitianNote: selection.dietitianNote,
          removedIngredients: selection.removedIngredients as any,
        });

        setSelectionCache((state) => {
          let temp = [...state];
          const index = temp.findIndex((i) => i.selectionId === meal.id);
          temp[index].process = false;
          temp[index].updated = false;
          temp[index].added = true;
          // set the new selection id from backend
          temp[index].selectionId = String(res.data.id);
          return temp;
        });
        setUserSelections((state) => {
          let temp = [...state];
          const index = temp.findIndex((i) => i.id === meal.id);
          temp[index].id = String(res.data.id);
          temp[index].portion = { ...temp[index].meal?.portions?.find((i) => i.portion === selection.selectedPortion) };
          temp[index].dietitianNote = selection.dietitianNote;
          temp[index].removedIngredients = [...(selection?.removedIngredients as string[])] as any;
          return temp;
        });
      } catch (error: any) {
        dispatch(
          setNotification({
            show: true,
            severity: "error",
            message: error.response?.data?.message || error.message,
          })
        );
        setSelectionCache((state) => {
          let temp = [...state];
          const index = temp.findIndex((i) => i.selectionId === meal.id);
          temp[index].process = false;
          return temp;
        });
      }
    } else {
      try {
        const res = await Api.UserMenu.updateSelectionByAdmin(String(id), String(meal.id), {
          portion:
            selection?.portionType === AUMSSelectionPortionResponseTypeEnum.Default
              ? Number(selection?.selectedPortion)
              : undefined,
          dietitianNote: selection?.dietitianNote,
          removedIngredients: selection?.removedIngredients as any,
        });
        setRetriggerMealPlanReq(generateRandom());
      } catch (error: any) {
        dispatch(
          setNotification({
            show: true,
            severity: "error",
            message: error.response?.data?.message || error.message,
          })
        );
        setSelectionCache((state) => {
          let temp = [...state];
          const index = temp.findIndex((i) => i.selectionId === meal.id);
          temp[index].process = false;
          return temp;
        });
      }
    }
  };
  const handleDeleteMeal = async (meal: AdminUserMenuSelectionResponse) => {
    // check if the selection is added on the fly if yes, delete it in the client
    // if no, send a delete req

    const selectionIndex = selectionCache.findIndex((i) => i.selectionId === meal.id);
    const selectionItem = selectionCache[selectionIndex];

    if (selectionItem.added !== false) {
      try {
        setSelectionCache((state) => {
          let temp = [...state];
          temp[selectionIndex].process = true;
          return temp;
        });
        const res = await Api.UserMenu.removeSelectionByAdmin(String(id), selectionItem.selectionId);
      } catch (error: any) {
        dispatch(
          setNotification({
            show: true,
            severity: "error",
            message: error.response?.data?.message || error.message,
          })
        );
        setSelectionCache((state) => {
          let temp = [...state];
          temp[selectionIndex].process = false;
          return temp;
        });
        return;
      }
    }
    setSelectionCache((state) => {
      let temp = [...state];
      temp.splice(selectionIndex, 1);
      return temp;
    });
    setUserSelections((state) => {
      let temp = [...state];
      const findIndex = temp.findIndex((i) => i.id === meal.id);
      temp.splice(findIndex, 1);
      return temp;
    });
  };

  const handleNextDay = () => {
    setCurrentDateFrame((state) => {
      let temp = [...state];
      temp.push(moment(temp[temp.length - 1]).add(1, "days"));
      temp.shift();
      return temp;
    });
  };
  const handlePreviousDay = () => {
    setCurrentDateFrame((state) => {
      let temp = [...state];
      temp.unshift(moment(temp[0]).subtract(1, "days"));
      temp.pop();
      return temp;
    });
  };

  const handleOpenSubscriptionCrud = () => {
    setSubscriptionCrudOpen(true);
  };

  const handleSubscriptionClose = () => {
    setSubscriptionCrudOpen(false);
    setSubscriptionEditOpen(false);
  };

  const handleEditPlan = (plan: AdminSubscriptionDetailResponse) => {
    setPlanToEdit(plan);
    setSubscriptionEditOpen(true);
  };

  const handleSubscriptionConfirm = () => {
    setSubscriptionCrudOpen(false);
    setRetriggerSubscriptionReq(generateRandom());
    setRetriggerMealPlanReq(generateRandom());
  };
  const handleSubscriptionEdit = () => {
    setSubscriptionEditOpen(false);
    setRetriggerSubscriptionReq(generateRandom());
    setRetriggerMealPlanReq(generateRandom());
  };

  const downloadInvoice = async (plan: AdminSubscriptionDetailResponse) => {
    setInvoiceDownloadProcess(true);
    try {
      const res = await Api.Invoice.downloadInvoice(String(plan.invoiceId), {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      saveAs(blob, `rejeem-invoice-${plan.invoiceId}.pdf`);
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    }
    setInvoiceDownloadProcess(false);
  };

  // PLAN START & STOP FUNCTIONALITY
  const [stopPanelOpen, setStopPanelOpen] = useState({ visibility: false, mode: "stop" });
  const [resumePanelOpen, setResumePanelOpen] = useState(false);
  const [subscriptionInfoText, setSubscriptionInfoText] = useState("");
  const [targetPlan, setTargetPlan] = useState<AdminSubscriptionDetailResponse>();
  const [tabModeValue, setTabModeValue] = useState(0);
  const [calendarDate, setCalendarDate] = useState<Moment | null>();
  const [calendarMinDate, setCalendarMinDate] = useState<Moment | undefined>(moment(lockedStartGlobal));
  const [calendarMaxDate, setCalendarMaxDate] = useState<Moment | undefined>();
  const [calendarError, setCalendarError] = useState<CalendarError>();
  const [additionLimitWarning, setAdditionLimitWarning] = useState(false);
  const [selectedPauseDays, setSelectedPauseDays] = useState<DateSelection[]>([]);
  const [selectedPauseDaysReference, setSelectedPauseDaysReference] = useState<DateSelection[]>([]);
  const [datePickerValue, setDatePickerValue] = useState<DateSelection>({ startDate: null, endDate: null });

  const handleStopPanelOpen = (plan: AdminSubscriptionDetailResponse) => {
    let lockedStart = moment(lockedStartGlobal);
    if (moment(plan.startDate).isAfter(lockedStart)) {
      lockedStart = moment(plan.startDate);
    }

    setCalendarMinDate(lockedStart);
    setCalendarMaxDate(moment(plan.endDate));
    setTargetPlan(plan);

    const targetSubscription = userSubscriptions?.find((subs) => subs.id === plan.id);
    const pausedSubscriptions = targetSubscription?.actions?.filter(
      (subs) =>
        subs.type === "PAUSE" && (subs.endDate === null || moment(subs.endDate).isSameOrAfter(moment(plan.startDate)))
    );
    const selectedPauseDays = pausedSubscriptions
      ? pausedSubscriptions.map(
          (subs) =>
            ({
              id: subs.id,
              disabled: subs.endDate !== null && moment(subs.startDate).isBefore(lockedStart),
              startDate: subs.startDate,
              endDate: subs.endDate,
            } as DateSelection)
        )
      : [];

    setSelectedPauseDays(selectedPauseDays);
    setSelectedPauseDaysReference(selectedPauseDays);
    setStopPanelOpen({ visibility: true, mode: "stop" });
  };
  const handleStopPanelClose = () => {
    setStopPanelOpen({ visibility: false, mode: "" });
    setDatePickerValue({ startDate: null, endDate: null });
    setSelectedPauseDays([]);
  };

  const handleCancelSubscription = async (subscriptionId: string | undefined, balanceType: AdminSubscriptionDetailResponseBalanceTypeEnum | undefined) => {
    const answer = balanceType === AdminSubscriptionDetailResponseBalanceTypeEnum.Actual ? await confirmActualSubscriptionCancel() : await confirmSubscriptionCancel();
    if (!answer) return;

    try {
      const res = await Api.Subscription.cancelSubscription(String(subscriptionId));
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    }
    setRetriggerSubscriptionReq(generateRandom());
    setRetriggerMealPlanReq(generateRandom());
  };

  const handleResumePanelOpen = (plan: AdminSubscriptionDetailResponse) => {
    setTargetPlan(plan);
    setResumePanelOpen(true);
  };
  const handleResumePanelClose = () => {
    setResumePanelOpen(false);
  };

  const handleEditPanelOpen = (plan: AdminSubscriptionDetailResponse) => {
    let lockedStart = moment(lockedStartGlobal);
    if (moment(plan.startDate).isAfter(lockedStart)) {
      lockedStart = moment(plan.startDate);
    }

    setCalendarMinDate(lockedStart);
    setCalendarMaxDate(moment(plan.endDate));

    const targetSubscription = userSubscriptions?.find((subs) => subs.id === plan.id);
    const pausedSubscriptions = targetSubscription?.actions?.filter(
      (subs) =>
        subs.type === "PAUSE" && (subs.endDate === null || moment(subs.endDate).isSameOrAfter(moment(plan.startDate)))
    );
    const selectedPauseDays = pausedSubscriptions
      ? pausedSubscriptions.map(
          (subs) =>
            ({
              id: subs.id,
              disabled: subs.endDate !== null && moment(subs.endDate).isBefore(lockedStart),
              startDate: subs.startDate,
              endDate: subs.endDate,
            } as DateSelection)
        )
      : [];

    setSelectedPauseDays(selectedPauseDays);
    setSelectedPauseDaysReference(selectedPauseDays);

    setTargetPlan(plan);
    setStopPanelOpen({ visibility: true, mode: "edit" });
  };

  useEffect(() => {
    if (selectedPauseDays.length) {
      setSubscriptionInfoText(
        `The plan will be paused for the dates: ${selectedPauseDays
          .map((day) =>
            day.startDate === day.endDate
              ? moment(day.startDate).format(displayFormat)
              : moment(day.startDate).format(displayFormat) +
                "-" +
                (day.endDate ? moment(day.endDate).format(displayFormat) : "Continuous")
          )
          .join(", ")}`
      );
    } else {
      setSubscriptionInfoText("No dates selected");
    }
    if (selectedPauseDays.filter((day) => !day.id).length === 10) {
      setAdditionLimitWarning(true);
    } else {
      setAdditionLimitWarning(false);
    }
  }, [selectedPauseDays]);

  const handleCalendarDateChange = (value: Moment | null) => {
    setCalendarDate(value);
    setSelectedPauseDays((state) => {
      const temp = [...state];
      const item = String(value?.format(defaultFormat));
      const foundIndex = temp.findIndex((i) => i.startDate === item && i.endDate === item);
      if (foundIndex > -1) {
        temp.splice(foundIndex, 1);
      } else {
        if (temp.filter((day) => !day.id).length !== 10) {
          temp.push({ startDate: item, endDate: item });
        }
      }
      return temp;
    });
  };

  const addRange = () => {
    if (
      !moment(datePickerValue.startDate).isValid() ||
      (datePickerValue.endDate !== null && !moment(datePickerValue.endDate).isValid()) ||
      datePickerValue.startDate === null ||
      (datePickerValue.endDate !== null && moment(datePickerValue.endDate).isBefore(moment(datePickerValue.startDate)))
    )
      return;

    if (checkIf(calendarError?.startDate) || checkIf(calendarError?.endDate)) return;

    const startDate = moment(datePickerValue.startDate).format(defaultFormat);
    const endDate = datePickerValue.endDate ? moment(datePickerValue.endDate).format(defaultFormat) : null;

    setSelectedPauseDays((state) => {
      const temp = [...state];
      const foundIndex = temp.findIndex((i) => i.startDate === startDate && i.endDate === endDate);
      if (foundIndex === -1) {
        if (temp.filter((day) => !day.id).length !== 10) {
          temp.push({ startDate, endDate });
        }
      }
      return temp;
    });
  };

  const deletePausedItem = (datePickerValue: DateSelection) => {
    const startDate = moment(datePickerValue.startDate).format(defaultFormat);
    const endDate = datePickerValue.endDate ? moment(datePickerValue.endDate).format(defaultFormat) : null;

    setSelectedPauseDays((state) => {
      const temp = [...state];
      const foundIndex = temp.findIndex((i) => i.startDate === startDate && i.endDate === endDate);
      if (foundIndex > -1) {
        temp.splice(foundIndex, 1);
      }
      return temp;
    });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabModeValue(newValue);
  };

  const stopPlan = async () => {
    setLoading(true);

    for (const pauseDay of selectedPauseDays) {
      if (pauseDay?.disabled !== true) {
        try {
          const res = await Api.Subscription.pauseSubscriptionByAdmin(String(targetPlan?.id), {
            startDate: String(pauseDay.startDate),
            endDate: pauseDay.endDate === null ? undefined : String(pauseDay.endDate),
          });
        } catch (error: any) {
          dispatch(
            setNotification({
              show: true,
              severity: "error",
              message: error.response?.data?.message || error.message,
            })
          );
        }
      }
    }
    setRetriggerSubscriptionReq(generateRandom());
    setRetriggerMealPlanReq(generateRandom());
    handleStopPanelClose();
    setLoading(false);
  };
  const resumePlan = async () => {
    setLoading(true);
    try {
      const res = await Api.Subscription.deleteSubscriptionActionByAdmin(
        String(targetPlan?.id),
        String(targetPlan?.actions?.[0].id)
      );
      setRetriggerSubscriptionReq(generateRandom());
      setRetriggerMealPlanReq(generateRandom());
      handleResumePanelClose();
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    }
    setLoading(false);
  };
  const editPlan = async () => {
    setLoading(true);

    // compare the reference and the selectedPauseDays
    let payload: (DateSelection & { action?: string })[] = [];

    selectedPauseDaysReference.forEach((reference) => {
      let found = false;
      selectedPauseDays.forEach((selected) => {
        if (reference.id === selected.id) {
          found = true;
          payload.push({ ...reference, action: "keep" });
        }
      });
      if (!found) {
        payload.push({ ...reference, action: "delete" });
      }
    });

    payload = [...payload, ...selectedPauseDays.filter((selected) => !Boolean(selected.id))];

    for (const pauseDay of payload) {
      const checkLockedStart = moment(lockedStartGlobal);
      const checkIfStartDateIsPast = moment(pauseDay.startDate).isBefore(checkLockedStart);
      try {
        if (pauseDay.id && pauseDay.action === "delete" && pauseDay.disabled === false) {
          // check if we are deleting a range with start date past
          // in this case, we need to update the enddate to today + lockedDaysNumber
          if (checkIfStartDateIsPast) {
            const newEndDate = moment().startOf("day").add(lockedDaysNumber, "days");
            const payload = {
              startDate: pauseDay.startDate,
              endDate: newEndDate.format(defaultFormat),
            };
            await Api.Subscription.updateSubscriptionActionByAdmin(String(targetPlan?.id), pauseDay.id, payload);
          } else {
            await Api.Subscription.deleteSubscriptionActionByAdmin(String(targetPlan?.id), pauseDay.id);
          }
        }
        if (!Boolean(pauseDay.id)) {
          await Api.Subscription.pauseSubscriptionByAdmin(String(targetPlan?.id), {
            startDate: String(pauseDay.startDate),
            endDate: pauseDay.endDate === null ? undefined : String(pauseDay.endDate),
          });
        }
      } catch (error: any) {
        dispatch(
          setNotification({
            show: true,
            severity: "error",
            message: error.response?.data?.message || error.message,
          })
        );
      }
    }

    setRetriggerSubscriptionReq(generateRandom());
    setRetriggerMealPlanReq(generateRandom());
    handleStopPanelClose();
    setLoading(false);
  };
  const deleteAllActions = async () => {
    setSelectedPauseDays([]);
    await editPlan();
  };

  // Payment History
  const [refreshPaymentHistory, setRefreshPaymentHistory] = useState<number>();
  const paymentHistory = usePaymentHistory(userReference?.account?.id, refreshPaymentHistory);
  const [transactionCrudOpen, setTransactionCrudOpen] = useState({ show: false, type: "", edit: undefined });
  const [ConfirmTransactionDeleteDialog, confirmTransactionDelete] = useConfirm({
    titleIcon: <DeleteLabelIcon />,
    text: "You are about to permanently delete a transaction from the list, and this action is irreversible. Are you sure you want to proceed?",
  });

  const columns = useMemo(() => {
    return [
      {
        field: "operationType",
        flex: 0,
        minWidth: 40,
        maxWidth: 40,
        headerName: "",
        sortable: false,
        hideable: false,
        valueGetter: (params: any) => params.row.operationType,
        renderCell: (params: GridRenderCellParams<any, any>) => {
          if (params.value === "DEPOSIT") {
            return <DepositIcon />;
          } else if (params.value === "WITHDRAW") {
            return <WithdrawIcon />;
          }
        },
      },
      {
        field: "transactionType",
        flex: 1,
        headerName: "Type",
        hideable: false,
        valueGetter: (params: any) =>
          TransactionType[params.row.transactionType as AccountTransactionResponseTransactionTypeEnum] as any,
      },
      {
        field: "date",
        minWidth: 80,
        flex: 1,
        headerName: "Date",
        valueGetter: ((params: any) => moment(params.row.date).format(displayFormat)) as any,
      },
      {
        field: "amount",
        minWidth: 80,
        flex: 1,
        headerName: "Amount",
        hideable: false,
        valueGetter: ((params: any) => formatCurrency(Math.abs(params.row.amount))) as any,
      },
      {
        field: "paymentType",
        minWidth: 80,
        flex: 1,
        headerName: "Payment Type",
        valueGetter: ((params: any) =>
          PaymentType[params.row.paymentType as AccountTransactionResponsePaymentTypeEnum]) as any,
      },
      {
        field: "description",
        minWidth: 120,
        flex: 2,
        headerName: "Explanation",
        hideable: false,
        sortable: false,
      },
    ];
  }, [userReference]);
  const handleTransactionConfirm = () => {
    setTransactionCrudOpen({ show: false, type: "", edit: undefined });
    setRefreshPaymentHistory(generateRandom());
    setRetriggerReq(generateRandom());
  };
  const editPaymentHistoryRecord = (params: any) => {
    setTransactionCrudOpen({
      show: true,
      type: params.row.operationType.toLowerCase(),
      edit: { ...params.row, amount: Math.abs(params.row.amount) },
    });
  };

  const deletePaymentHistoryRecord = async (params: any) => {
    const answer = await confirmTransactionDelete();
    if (!answer) return;

    try {
      const req = await Api.Account.deleteTransaction(String(userReference?.account?.id), params.row.id);
      dispatch(
        setNotification({
          show: true,
          severity: "success",
          message: "Transaction has been deleted successfully",
        })
      );
      setRefreshPaymentHistory(generateRandom());
      setRetriggerReq(generateRandom());
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    }
  };

  const [DialogReview, confirmDialogReview] = useConfirm({
    titleIcon: <PortionLabelIcon width={32} height={32} outerWidth={64} />,
    text: "Please review the custom portion requests entered by the client.",
    yes: "Got it!",
    no: null,
  });

  const translateStatus = (status: AUMSCustomPortionRequestResponseStatusEnum) => {
    switch (status) {
      case AUMSCustomPortionRequestResponseStatusEnum.Adjusted:
        return "Adjusted";
      case AUMSCustomPortionRequestResponseStatusEnum.Approved:
        return "Approved";
      case AUMSCustomPortionRequestResponseStatusEnum.Cancelled:
        return "Canceled";
      case AUMSCustomPortionRequestResponseStatusEnum.Rejected:
        return "Rejected";
      case AUMSCustomPortionRequestResponseStatusEnum.Pending:
        return "Pending";
      default:
        break;
    }
  };

  const updateStatus = async () => {
    // check if any pending request is present
    const check = userSelections.some((selection) =>
      Boolean(
        selection.customPortionRequest &&
          selection.customPortionRequest.status === AUMSCustomPortionRequestResponseStatusEnum.Pending
      )
    );

    if (check) {
      await confirmDialogReview();
      return;
    }

    const newStatus = userMenu.status === "PENDING_REVIEW" ? "REVIEWED" : "PENDING_REVIEW";

    try {
      setUpdateStatusLoading(true);
      const res = await Api.UserMenu.updateStatusById(String(userMenu.id), newStatus);
      dispatch(
        setNotification({
          show: true,
          severity: "success",
          message:
            newStatus == "PENDING_REVIEW"
              ? "User's menu plan marked as pending review"
              : "User's menu plan marked as reviewed",
        })
      );

      setUserMenu({
        ...userMenu,
        status: newStatus,
      });
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    } finally {
      setUpdateStatusLoading(false);
    }
  };

  const updateActiveTab = (index: number) => {
    setSearchParams((searchParams) => {
      searchParams.set("activeTab", String(index));
      return searchParams;
    });
    setActiveTab(index);
  };

  const updateSelectedDate = (formattedDate: string) => {
    setSearchParams((searchParams) => {
      searchParams.set("date", formattedDate);
      return searchParams;
    });

    setSelectedDate(formattedDate);
  };

  const channelIcon = useMemo(() => {
    if (checkIf(userReference)) {
      return ChannelIcon(String(userReference?.channel));
    }
  }, [userReference]);

  const membershipCreateUserButton = useMemo(() => {
    return userReference?.channel !== "rejeem";
  }, [userReference]);

  // MEAL CUSTOM PORTION ADJUSTMENT
  const [portionCustomizeOpen, setPortionCustomizeOpen] = useState(false);
  const [selection, setSelection] = useState<AdminUserMenuSelectionResponse>();

  const portionCustomizeClose = (result?: boolean) => {
    if (result) {
      setRetriggerMealPlanReq(generateRandom());
    }
    setPortionCustomizeOpen(false);
  };

  // GOOGLE MAPS INTEGRATION WITH ZONES

  async function initMap() {
    google.maps.importLibrary("maps") as Promise<google.maps.MapsLibrary>;
    google.maps.importLibrary("marker");
    google.maps.importLibrary("places");
  }

  useEffect(() => {
    initMap();
  }, []);

  const [mapDisplay, setMapDisplay] = useState(false);
  const [selectedCoordinates, setSelectedCoordinates] = useState<{ lat: number; lng: number; detail: string }>();
  const [selectedAddress, setSelectedAddress] = useState<string>();

  const ChooseAddressFromMap = (place: any) => {
    if (place) {
      // console.log(place);
      handleAddressChange(String(selectedAddress), "detail", place.formattedAddress);
      handleAddressChange(String(selectedAddress), "location", {
        lat: place.location.lat(),
        lon: place.location.lng(),
      });
      setSelectedCoordinates({
        lat: place.location.lat(),
        lng: place.location.lng(),
        detail: place.formattedAddress,
      });
    }
    setMapDisplay(false);
  };

  // console.log("pausedDays", selectedPauseDays);
  // console.log("user reference", userReference);
  // console.log("paused dates", selectedPauseDays);
  // console.log("addressList", addressList);
  // console.log("addressListRef", addressListRef);
  // console.log("address active tab", addressActiveTab);
  // console.log("channelIcon", channelIcon);
  // console.log("user", user);
  // console.log("validation", validation);

  return (
    <Box className="users">
      {/* Remove this part when called as component */}
      {checkIf(props.userId) ? null : (
        <Box className="section-breadcrumb">
          <ArrowLeftIcon onClick={back} />
          <Typography component={"span"}>User Detail</Typography>
        </Box>
      )}
      {/* Remove this part when called as component */}
      <Box p={6} className={`users-container`}>
        {loading && <MainSpinner />}
        <Box className={`user-detail-upper ${loading ? "filter" : ""}`}>
          <Box className="user-info">
            <Box className="identifier">
              <Box sx={{ display: "flex", gap: "16px", alignItems: "center" }}>
                <Typography className="name">{userReference?.fullName}</Typography>

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                  <LightTooltip
                    title={
                      <Box sx={{ display: "flex", flexDirection: "column", rowGap: "4px" }}>
                        {membershipUsers && membershipUsers.length > 0 ? (
                          <>
                            <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "text.dark" }}>
                              Users of this membership
                            </Typography>
                            <Typography sx={{ fontSize: "12px", fontWeight: 500, color: "text.fontLight3" }}>
                              {membershipUsers.length} users
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", flexWrap: "wrap" }}>
                              {membershipUsers?.map((user, index) => (
                                <Box
                                  key={user.id}
                                  sx={{
                                    width: "100%",
                                    borderTop: "1px solid var(--table-color)",
                                    display: "flex",
                                    flexDirection: "row",
                                    gap: "8px",
                                    // justifyContent: "center",
                                    alignItems: "center",
                                    padding: "12px 0",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      justifyContent: "center",
                                      alignItems: "center",
                                      borderRadius: "8px",
                                      width: "32px",
                                      height: "32px",
                                      backgroundColor: "text.tableColor",
                                    }}
                                  >
                                    {getInitials(user.fullName || "")}
                                  </Box>
                                  <Typography
                                    sx={{
                                      borderRadius: "4px",
                                      padding: "2px 4px",
                                      fontSize: "12px",
                                      fontWeight: 500,
                                      lineHeight: "16px",
                                    }}
                                  >
                                    {user.fullName}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </>
                        ) : (
                          <Typography sx={{ fontSize: "12px", fontWeight: 400 }}>
                            User is not a member of any membership.
                          </Typography>
                        )}

                        {membershipCreateUserButton ? (
                          <Button
                            variant="contained"
                            color="secondary"
                            size="small"
                            sx={{ fontSize: "12px", fontWeight: 500, height: 28, textTransform: "none" }}
                            onClick={() => {
                              setMultipleUserDialog({
                                open: true,
                                // membershipInfo: { id: String(user?.membershipId), phoneNumber: user?.membership?.phoneNumber },
                              });
                            }}
                            startIcon={<PlusSignIcon width={16} height={16} />}
                          >
                            Create User
                          </Button>
                        ) : null}
                      </Box>
                    }
                    placement="top"
                    arrow
                  >
                    <Typography
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "4px 6px",
                        backgroundColor: "text.tableColor",
                        borderRadius: "4px",
                        color: "text.fontDark",
                      }}
                    >
                      <UsersAddIcon width={24} height={24} />
                    </Typography>
                  </LightTooltip>
                </Box>
              </Box>
              <Typography className={activity.class} component={"span"}>
                {activity.value}
              </Typography>
            </Box>
            <Box className="status">
              <Box className="id-container">
                <Typography className="title">External ID:</Typography>
                <Typography className="ext-id">{userReference?.externalId}</Typography>
                <CopyToClipboard text={String(userReference?.externalId)} onCopy={() => setCopied(true)}>
                  <CopyIcon width={16} height={16} />
                </CopyToClipboard>
                {copied ? <CheckIcon width={16} height={16} /> : null}
              </Box>
              <Box className="balance-container">
                <Typography className="title">Balance:</Typography>
                <Typography className="exp">{formatCurrency(userReference?.account?.totalBalance)}</Typography>
              </Box>
              {channelIcon ? <Box sx={{ display: "flex", alignItems: "center" }}> {channelIcon} </Box> : null}
            </Box>
          </Box>
          <Box className="seperator"></Box>
          <Box className="tabs-container">
            <Box className={`tab ${activeTab === 0 ? "active" : ""}`} onClick={() => updateActiveTab(0)}>
              Personal Details
            </Box>
            <Can permission="USER_SUBSCRIPTION_VIEW">
              <Box className={`tab ${activeTab === 1 ? "active" : ""}`} onClick={() => updateActiveTab(1)}>
                Subscriptions
              </Box>
            </Can>
            {userSubscriptions?.length > 0 && (
              <Can permission="USER_MEAL_PLAN_VIEW">
                <Box className={`tab ${activeTab === 2 ? "active" : ""}`} onClick={() => updateActiveTab(2)}>
                  Meal Plan
                </Box>
              </Can>
            )}
            <Can permission="USER_PAYMENT_HISTORY_VIEW">
              <Box className={`tab ${activeTab === 3 ? "active" : ""}`} onClick={() => updateActiveTab(3)}>
                Payment History
              </Box>
            </Can>
          </Box>
        </Box>
        {activeTab === 0 && (
          <Box className={`user-detail-lower ${loading ? "filter" : ""}`}>
            <Box className="tabs-container">
              <Box
                className={`tab ${userDetailActiveTab === 0 ? "active" : ""}`}
                onClick={() => setUserDetailActiveTab(0)}
              >
                Personal Info
              </Box>

              <Can permission="USER_ADDRESS_VIEW">
                <Box
                  className={`tab ${userDetailActiveTab === 1 ? "active" : ""}`}
                  onClick={() => setUserDetailActiveTab(1)}
                >
                  Addresses
                </Box>
              </Can>
              <Box
                className={`tab ${userDetailActiveTab === 2 ? "active" : ""}`}
                onClick={() => setUserDetailActiveTab(2)}
              >
                Allergens
              </Box>
              <Box
                className={`tab ${userDetailActiveTab === 3 ? "active" : ""}`}
                onClick={() => setUserDetailActiveTab(3)}
              >
                Medical Conditions
              </Box>
              <Box
                className={`tab ${userDetailActiveTab === 4 ? "active" : ""}`}
                onClick={() => setUserDetailActiveTab(4)}
              >
                Dislikes
              </Box>
            </Box>

            {userDetailActiveTab === 0 && (
              <>
                <Grid container spacing={12} mt={0}>
                  <Grid item md={6} lg={5} sx={{ paddingTop: "20px !important" }}>
                    <Box py={0} sx={{ display: "flex", flexDirection: "column", rowGap: "16px" }}>
                      <Box className="form-section">
                        <Typography className="form-label-title">Personal Details</Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "baseline", columnGap: "12px" }}>
                        <Box className="form-section">
                          <Typography component="span" className="form-label">
                            Name / Surname
                          </Typography>
                          <TextField
                            className="form-field"
                            value={user?.fullName}
                            error={validation.show && validation.fullName?.error}
                            helperText={
                              validation.show && validation.fullName?.error ? validation.fullName.helperText : undefined
                            }
                            onChange={(e) => handleFieldChange("fullName", e.target.value)}
                          />
                        </Box>
                        <Box className="form-section">
                          <Typography component="span" className="form-label">
                            External ID
                          </Typography>
                          <TextField
                            className="form-field"
                            value={user?.externalId}
                            onChange={(e) => handleFieldChange("externalId", e.target.value)}
                          />
                        </Box>
                      </Box>
                      <Box className="form-section">
                        <Typography component="span" className="form-label">
                          Telephone
                        </Typography>
                        <Box sx={{ display: "flex", columnGap: "16px" }}>
                          <Box className="form-section">
                            <TextField
                              inputRef={phoneNumberRef}
                              className="form-field"
                              value={user?.phoneNumber?.slice(4)}
                              onChange={(e) => handleFieldChange("phoneNumber", `+974${e.target.value}`)}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">+974</InputAdornment>,
                                endAdornment: (
                                  <InputAdornment sx={{ marginRight: "8px" }} position="end">
                                    <Tooltip title={"Edit Phone Number"} placement="top" arrow>
                                      <IconButton
                                        onClick={() => {
                                          setEnablePhoneNumberChange(true);
                                          phoneNumberRef.current?.focus();
                                        }}
                                        edge="end"
                                      >
                                        <Edit2Icon />
                                      </IconButton>
                                    </Tooltip>
                                  </InputAdornment>
                                ),
                                readOnly: !enablePhoneNumberChange,
                              }}
                              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                              error={validation.show && validation.phoneNumber?.error}
                              helperText={
                                validation.show && validation.phoneNumber?.error
                                  ? validation.phoneNumber.helperText
                                  : undefined
                              }
                            />
                          </Box>
                        </Box>
                      </Box>

                      <Box sx={{ display: "flex", columnGap: "12px" }}>
                        <Box className="form-section">
                          <Typography component="span" className="form-label">
                            Delivery Telephone
                          </Typography>
                          <Box className="form-section">
                            <TextField
                              className="form-field"
                              value={user?.deliveryPhoneNumber?.slice(4)}
                              onChange={(e) => handleFieldChange("deliveryPhoneNumber", `+974${e.target.value}`)}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">+974</InputAdornment>,
                              }}
                              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                              error={validation.show && validation.deliveryPhoneNumber?.error}
                              helperText={
                                validation.show && validation.deliveryPhoneNumber?.error
                                  ? validation.deliveryPhoneNumber.helperText
                                  : undefined
                              }
                            />
                          </Box>
                        </Box>

                        <Box className="form-section">
                          <Typography component="span" className="form-label">
                            Date of Birth
                          </Typography>

                          <DatePicker
                            sx={{
                              "& .MuiFormHelperText-root": {
                                bottom: "-20px",
                                left: 0,
                                right: 0,
                                color: "text.error",
                              },
                            }}
                            value={startDate}
                            open={open}
                            format={displayFormat}
                            onChange={(newValue) => handleChangeBirthdate(newValue as Moment)}
                            onAccept={() => setOpen(false)}
                            onClose={() => setOpen(false)}
                            onError={(newError) => setDateSelectionError(newError)}
                            shouldDisableDate={(day: Moment) => disableDayCheck(day)}
                            ref={calendarRef}
                            slotProps={{
                              textField: {
                                onClick: () => setOpen(true),
                                helperText:
                                  validation.show && validation.birthDate?.error
                                    ? validation.birthDate.helperText
                                    : errorMessage || null,
                              },
                            }}
                          />
                        </Box>
                      </Box>

                      <Box className="form-section">
                        <Typography component="span" className="form-label">
                          Gender
                        </Typography>
                        <RadioGroup
                          className="form-field"
                          sx={{ display: "flex", flexDirection: "row" }}
                          value={user?.gender || null}
                          onChange={(e) => handleFieldChange("gender", e.target.value)}
                        >
                          <FormControlLabel
                            className={user?.gender === PartnerUserDetailResponseGenderEnum.Male ? "selected" : ""}
                            value={PartnerUserDetailResponseGenderEnum.Male}
                            control={<RadioBoxCustom />}
                            label="Male"
                          />
                          <FormControlLabel
                            className={user?.gender === PartnerUserDetailResponseGenderEnum.Female ? "selected" : ""}
                            value={PartnerUserDetailResponseGenderEnum.Female}
                            control={<RadioBoxCustom />}
                            label="Female"
                          />
                        </RadioGroup>
                        {validation.show && validation.gender?.error ? (
                          <FormHelperText sx={{ color: "text.error" }}>{validation.gender.helperText}</FormHelperText>
                        ) : null}
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "baseline", columnGap: "12px" }}>
                        <Box className="form-section">
                          <Typography component="span" className="form-label">
                            Height
                          </Typography>
                          <TextField
                            className="form-field"
                            value={user?.height}
                            error={validation.show && validation.height?.error}
                            helperText={
                              validation.show && validation.height?.error ? validation.height.helperText : undefined
                            }
                            onChange={(e) => handleFieldChange("height", e.target.value)}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                            }}
                          />
                        </Box>
                        <Box className="form-section">
                          <Typography component="span" className="form-label">
                            Weight
                          </Typography>
                          <TextField
                            className="form-field"
                            value={user?.weight}
                            error={validation.show && validation.weight?.error}
                            helperText={
                              validation.show && validation.weight?.error ? validation.weight.helperText : undefined
                            }
                            onChange={(e) => handleFieldChange("weight", e.target.value)}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">kg</InputAdornment>,
                            }}
                          />
                        </Box>
                        <Box className="form-section">
                          <Typography component="span" className="form-label">
                            Target Weight
                          </Typography>
                          <TextField
                            className="form-field"
                            value={user?.targetWeight}
                            error={validation.show && validation.targetWeight?.error}
                            helperText={
                              validation.show && validation.targetWeight?.error ? validation.targetWeight.helperText : undefined
                            }
                            onChange={(e) => handleFieldChange("targetWeight", e.target.value)}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">kg</InputAdornment>,
                            }}
                          />
                        </Box>
                      </Box>
                      {affiliateList && affiliateList.length > 0 ? (
                        <Box className="form-section">
                          <Typography component="span" className="form-label">
                            Affiliate
                          </Typography>
                          <Select
                            value={user?.affiliateId || " "}
                            onChange={(e) => handleFieldChange("affiliateId", e.target.value)}
                          >
                            <MenuItem key={"0"} value={" "}>
                              None
                            </MenuItem>
                            {affiliateList?.map((affiliateItem) => (
                              <MenuItem key={affiliateItem.id} value={affiliateItem.id}>
                                {affiliateItem.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </Box>
                      ) : null}

                      <Box py={0} sx={{ display: "flex", flexDirection: "column", rowGap: "16px" }}>
                        <Box className="form-section">
                          <Typography className="form-label-title">Delivery</Typography>
                        </Box>
                        <Box className="form-section">
                          <Typography component="span" className="form-label">
                            Delivery Notes
                          </Typography>
                          <TextField
                            sx={{
                              "& .MuiInputBase-root > textarea,& .MuiInputBase-root": {
                                height: "auto",
                                minHeight: "56px",
                              },
                            }}
                            onChange={(e) => handleFieldChange("deliveryNote", e.target.value)}
                            className="form-field"
                            multiline
                            value={user?.deliveryNote}
                          />
                        </Box>
                      </Box>

                      <Box className="form-section">
                        <FormControlLabel
                          sx={{
                            margin: 0,
                            columnGap: "8px",
                            "& .MuiFormControlLabel-label": { fontSize: "14px", fontWeight: 500 },
                          }}
                          control={
                            <Toggle
                              checked={Boolean(user?.active)}
                              onChange={(e) => {
                                setFormDirty(true);
                                setUserEdit((state: any) => ({ ...state, active: Boolean(e.target.checked) }));
                              }}
                            />
                          }
                          label="Make the user Active"
                        />
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item md={6} lg={5} sx={{ paddingTop: "20px !important" }}>
                    {activeSubscription?.id ? (
                      <Box sx={{ display: "flex", flexDirection: "column", rowGap: "16px", marginTop: "16px" }}>
                        <Box className="form-section">
                          <Typography className="form-label-title">Dietary Targets</Typography>
                        </Box>
                        <Box sx={{ display: "flex", gap: "12px", flexDirection: "column" }}>
                          <Box sx={{ display: "flex", gap: "12px" }}>
                            <Box className="form-section">
                              <Typography component="span" className="form-label">
                                Target Calories
                              </Typography>
                              <TextField
                                className="form-field"
                                value={activeSubscription?.dietaryTargets?.calories}
                                error={subscriptionValidation.show && subscriptionValidation?.calories?.error}
                                helperText={
                                  subscriptionValidation.show && subscriptionValidation.calories?.error
                                    ? subscriptionValidation.calories?.helperText
                                    : undefined
                                }
                                onChange={(e) => handleDietaryTargetChange("calories", e.target.value)}
                                InputProps={{
                                  endAdornment: <InputAdornment position="end">cal</InputAdornment>,
                                }}
                              />
                            </Box>
                            <Box className="form-section">
                              <Typography component="span" className="form-label">
                                Protein
                              </Typography>
                              <TextField
                                className="form-field"
                                value={activeSubscription?.dietaryTargets?.protein}
                                error={subscriptionValidation.show && subscriptionValidation?.protein?.error}
                                helperText={
                                  subscriptionValidation.show && subscriptionValidation?.protein?.error
                                    ? subscriptionValidation.protein?.helperText
                                    : undefined
                                }
                                onChange={(e) => handleDietaryTargetChange("protein", e.target.value)}
                                InputProps={{
                                  endAdornment: <InputAdornment position="end">gr</InputAdornment>,
                                }}
                              />
                            </Box>
                          </Box>
                          <Box sx={{ display: "flex", gap: "12px" }}>
                            <Box className="form-section">
                              <Typography component="span" className="form-label">
                                Carbs
                              </Typography>
                              <TextField
                                className="form-field"
                                value={activeSubscription?.dietaryTargets?.carb}
                                error={subscriptionValidation.show && subscriptionValidation?.carb?.error}
                                helperText={
                                  subscriptionValidation.show && subscriptionValidation?.carb?.error
                                    ? subscriptionValidation.carb?.helperText
                                    : undefined
                                }
                                onChange={(e) => handleDietaryTargetChange("carb", e.target.value)}
                                InputProps={{
                                  endAdornment: <InputAdornment position="end">gr</InputAdornment>,
                                }}
                              />
                            </Box>
                            <Box className="form-section">
                              <Typography component="span" className="form-label">
                                Fat
                              </Typography>
                              <TextField
                                className="form-field"
                                value={activeSubscription?.dietaryTargets?.fat}
                                error={subscriptionValidation.show && subscriptionValidation?.fat?.error}
                                helperText={
                                  subscriptionValidation.show && subscriptionValidation?.fat?.error
                                    ? subscriptionValidation.fat?.helperText
                                    : undefined
                                }
                                onChange={(e) => handleDietaryTargetChange("fat", e.target.value)}
                                InputProps={{
                                  endAdornment: <InputAdornment position="end">gr</InputAdornment>,
                                }}
                              />
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    ) : null}

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        rowGap: "16px",
                        marginTop: "16px",
                        "& .MuiSelect-select": { fontSize: "14px", fontWeight: 500 },
                      }}
                    >
                      <Box className="form-section">
                        <Typography className="form-label-title">Personal Activity</Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: "12px", flexDirection: "column" }}>
                        <Box className="form-section">
                          <Typography component="span" className="form-label">
                            Dietitian
                          </Typography>
                          <Select value={user?.dietitianId || ""} onChange={dietitianChange} sx={{ marginBottom: 0 }}>
                            {dietitianList?.map((dietitianItem) => (
                              <MenuItem key={dietitianItem.id} value={dietitianItem.id}>
                                {dietitianItem.fullName}
                              </MenuItem>
                            ))}
                          </Select>
                        </Box>
                        <Box className="form-section">
                          <Typography component="span" className="form-label">
                            Diet Type
                          </Typography>
                          <Select
                            value={user?.dietType || ""}
                            onChange={(e) => handleFieldChange("dietType", e.target.value)}
                            sx={{ marginBottom: 0 }}
                          >
                            {Object.entries(PartnerUserDetailResponseDietTypeEnum).map(([key, value]) => (
                              <MenuItem key={key} value={value}>
                                {value.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                              </MenuItem>
                            ))}
                          </Select>
                        </Box>
                        <Box className="form-section">
                          <Typography component="span" className="form-label">
                            Dietary Goal
                          </Typography>
                          <Select
                            value={user?.dietGoal || ""}
                            onChange={(e) => handleFieldChange("dietGoal", e.target.value)}
                            sx={{ marginBottom: 0 }}
                          >
                            {Object.keys(dietGoals).map((key) => (
                              <MenuItem key={key} value={key}>
                                {dietGoals[key as keyof typeof dietGoals]}
                              </MenuItem>
                            ))}
                          </Select>
                        </Box>
                        <Box className="form-section">
                          <Typography component="span" className="form-label">
                            Activity Level
                          </Typography>
                          <Select
                            value={user?.activityLevel || ""}
                            onChange={(e) => handleFieldChange("activityLevel", e.target.value)}
                            sx={{ marginBottom: 0 }}
                          >
                            {Object.keys(activityLevels).map((key) => (
                              <MenuItem key={key} value={key}>
                                {activityLevels[key as keyof typeof activityLevels]}
                              </MenuItem>
                            ))}
                          </Select>
                          {user?.activityLevel ? (
                            <FormHelperText sx={{ color: "text.fontLight3", marginTop: "4px", marginBottom: "0px" }}>
                              <Box sx={{ display: "flex", alignItems: "flex-start", gap: "4px" }}>
                                <Typography
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "text.fontLight3",
                                    marginTop: "1px",
                                  }}
                                >
                                  <InfoIcon width={16} height={16} />
                                </Typography>
                                <Typography
                                  sx={{
                                    color: "text.fontLight3",
                                    fontSize: "12px",
                                    fontWeight: 500,
                                    lineHeight: "20px",
                                  }}
                                >
                                  {activityLevelOptions[user?.activityLevel as keyof typeof activityLevelOptions]}
                                </Typography>
                              </Box>
                            </FormHelperText>
                          ) : null}
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            borderRadius: "12px",
                            backgroundColor: "text.tableColor",
                            padding: "12px",
                            gap: "12px",
                          }}
                        >
                          <Box
                            sx={{
                              borderRadius: "8px",
                              display: "flex",
                              gap: "8px",
                              alignItems: "flex-start",
                              backgroundColor: "#fff",
                              flex: "1 1",
                              padding: "12px 16px",
                            }}
                          >
                            <Typography
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "text.dark",
                                marginTop: "1px",
                              }}
                            >
                              <ChartIcon width={24} height={24} />
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", rowGap: "4px" }}>
                              <Typography
                                sx={{ fontSize: "12px", fontWeight: 600, color: "text.dark", lineHeight: "20px" }}
                              >
                                Basal Metabolic Rate
                              </Typography>
                              <Typography
                                sx={{ fontSize: "14px", fontWeight: 500, color: "text.dark", lineHeight: "20px" }}
                              >
                                {user?.healthMetrics?.basalMetabolicRate}
                              </Typography>
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              borderRadius: "8px",
                              display: "flex",
                              gap: "8px",
                              alignItems: "flex-start",
                              backgroundColor: "#fff",
                              flex: "1 1",
                              padding: "12px 16px",
                            }}
                          >
                            <Typography
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "text.dark",
                                marginTop: "1px",
                              }}
                            >
                              <ChartUpIcon width={24} height={24} />
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", rowGap: "4px" }}>
                              <Typography
                                sx={{ fontSize: "12px", fontWeight: 600, color: "text.dark", lineHeight: "20px" }}
                              >
                                Recommended Calories
                              </Typography>
                              <Typography
                                sx={{ fontSize: "14px", fontWeight: 500, color: "text.dark", lineHeight: "20px" }}
                              >
                                {user?.healthMetrics?.recommendedCalories}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
                <Box className="form-section" sx={{ marginTop: "24px", maxWidth: "150px" }}>
                  <Can permission="USER_EDIT">
                    <LoadingButton
                      sx={{ padding: "12px 24px", fontSize: "14px" }}
                      color="primary"
                      disabled={!formDirty}
                      loading={saveLoading}
                      variant="contained"
                      disableElevation
                      onClick={saveChanges}
                    >
                      {saveLoading ? <span>&nbsp;</span> : <span>Save Changes</span>}
                    </LoadingButton>
                  </Can>
                </Box>
              </>
            )}
            {userDetailActiveTab === 1 && (
              <>
                <Grid container spacing={12} mt={0}>
                  <Grid item md={12} lg={6.5} sx={{ paddingTop: "20px !important" }}>
                    <Box py={0} sx={{ display: "flex", flexDirection: "column", rowGap: "16px" }}>
                      <Box className="form-section">
                        <Typography className="form-label-title">Address Details</Typography>
                      </Box>
                      <Box className="tabs-upper-container">
                        <Box className="address-list-container">
                          {addressList
                            // ?.sort((a, b) => (a.default === b.default ? 0 : a.default ? -1 : 1))
                            .map((listItem) => {
                              return (
                                <Box
                                  key={listItem.id}
                                  className={`address-tab ${addressActiveTab === listItem.id ? "active" : ""}`}
                                  onClick={() => addressTabChange(String(listItem.id))}
                                >
                                  {listItem.name}
                                </Box>
                              );
                            })}
                        </Box>
                        <Can permission="USER_ADDRESS_CREATE">
                          <Box
                            sx={{
                              opacity: `${addressList.length === 5 ? "0.5" : "1"}`,
                              pointerEvents: `${addressList.length === 5 ? "none" : "auto"}`,
                            }}
                            className="add-btn"
                            onClick={() => addressTabChange()}
                          >
                            <PlusIcon width={18} height={18} />
                            <Typography sx={{ fontSize: "12px", fontWeight: 500 }}>ADD</Typography>
                          </Box>
                        </Can>
                      </Box>

                      {addressList
                        .filter((i) => i.id === addressActiveTab)
                        .map((listItem) => {
                          return (
                            <Fragment key={listItem.id}>
                              <Box sx={{ display: "flex", alignItems: "flex-start", columnGap: "12px" }}>
                                <Box sx={{ display: "flex", flexDirection: "column", rowGap: "12px", flex: "1 1" }}>
                                  <Box className="form-section" sx={{ flex: "1 1" }}>
                                    <Box sx={{ display: "flex", gap: "4px", alignItems: "center" }}>
                                      <Typography component="span" className="form-label">
                                        Address Title
                                      </Typography>
                                      <Typography component="span" className="form-label-secondary">
                                        (Home, Office, Etc.)
                                      </Typography>
                                    </Box>

                                    <TextField
                                      className="form-field"
                                      value={listItem.name}
                                      error={addressValidation.show && addressValidation.name?.error}
                                      helperText={
                                        addressValidation.show && addressValidation.name?.error
                                          ? addressValidation.name.helperText
                                          : undefined
                                      }
                                      onChange={(e) => handleAddressChange(String(listItem.id), "name", e.target.value)}
                                    />
                                  </Box>
                                  <Box className="form-section" sx={{ flex: "1 1" }}>
                                    <Typography component="span" className="form-label">
                                      Default Address
                                    </Typography>
                                    <FormControlLabel
                                      sx={{
                                        margin: 0,
                                        columnGap: "8px",
                                        "& .MuiFormControlLabel-label": { fontSize: "14px", fontWeight: 500 },
                                      }}
                                      control={
                                        <Toggle
                                          disabled={addressListRef?.find((i) => i.id === listItem.id)?.default}
                                          checked={Boolean(listItem.default)}
                                          onChange={(e) =>
                                            handleAddressChange(String(listItem.id), "default", e.target.checked)
                                          }
                                        />
                                      }
                                      label=""
                                    />
                                  </Box>
                                </Box>
                                <Box
                                  sx={{
                                    flex: "1 1",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    borderRadius: "12px",
                                    marginBottom: "8px",
                                    cursor: "pointer",
                                    paddingTop: "104px",
                                    backgroundImage: "url(../../images/map.jpg)",
                                    backgroundSize: "cover",
                                    position: "relative",
                                  }}
                                  onClick={() => {
                                    setSelectedAddress(listItem.id);
                                    setMapDisplay(true);
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: "8px",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      position: "absolute",
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      backgroundColor: "text.tableColor",
                                      borderRadius: "12px",
                                      padding: "8px 16px",
                                    }}
                                  >
                                    <Typography
                                      sx={{ color: "text.fontLight3", display: "flex", alignItems: "center" }}
                                    >
                                      <LocationIcon />
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontSize: "14px",
                                        fontWeight: 500,
                                        lineHeight: "normal",
                                        color: "text.fontLight3",
                                      }}
                                    >
                                      Choose from the map
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>

                              <Box sx={{ display: "flex", alignItems: "baseline", columnGap: "12px" }}>
                                <Box sx={{ display: "flex", gap: "12px", flex: "1 1" }}>
                                  <Box className="form-section" sx={{ flex: 2 }}>
                                    <Typography component="span" className="form-label">
                                      Zone
                                    </Typography>
                                    <Autocomplete
                                      className="form-field"
                                      value={listItem.zone || null}
                                      getOptionLabel={(item) => item.toString()}
                                      options={zones}
                                      onChange={(event: any, newValue: number | null) => {
                                        handleAddressChange(String(listItem.id), "zone", newValue);
                                      }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          error={addressValidation.show && addressValidation.zone?.error}
                                          helperText={
                                            addressValidation.show && addressValidation.zone?.error
                                              ? addressValidation.zone.helperText
                                              : undefined
                                          }
                                        />
                                      )}
                                    />
                                  </Box>
                                  <Box className="form-section" sx={{ flex: 3 }}>
                                    <Typography component="span" className="form-label">
                                      Area
                                    </Typography>
                                    <Autocomplete
                                      className="form-field"
                                      value={areas.find((area) => area.name === listItem.area) || null}
                                      getOptionLabel={(item) => item.name || ""}
                                      options={areas}
                                      onChange={(event: any, newValue: AreaBriefResponse | null) => {
                                        handleAddressChange(String(listItem.id), "area", newValue?.name || null);
                                      }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          error={addressValidation.show && addressValidation.area?.error}
                                          helperText={
                                            addressValidation.show && addressValidation.area?.error
                                              ? addressValidation.area.helperText
                                              : undefined
                                          }
                                        />
                                      )}
                                    />
                                  </Box>
                                </Box>

                                <Box className="form-section" sx={{ flex: "1 1" }}>
                                  <Typography component="span" className="form-label">
                                    Street
                                  </Typography>
                                  <TextField
                                    className="form-field"
                                    value={listItem.street}
                                    error={addressValidation.show && addressValidation.street?.error}
                                    helperText={
                                      addressValidation.show && addressValidation.street?.error
                                        ? addressValidation.street.helperText
                                        : undefined
                                    }
                                    onChange={(e) => handleAddressChange(String(listItem.id), "street", e.target.value)}
                                  />
                                </Box>
                              </Box>
                              <Box sx={{ display: "flex", alignItems: "baseline", columnGap: "12px" }}>
                                <Box className="form-section">
                                  <Typography component="span" className="form-label">
                                    Building
                                  </Typography>
                                  <TextField
                                    className="form-field"
                                    value={listItem.building}
                                    error={addressValidation.show && addressValidation.building?.error}
                                    helperText={
                                      addressValidation.show && addressValidation.building?.error
                                        ? addressValidation.building.helperText
                                        : undefined
                                    }
                                    onChange={(e) =>
                                      handleAddressChange(String(listItem.id), "building", e.target.value)
                                    }
                                  />
                                </Box>
                                <Box className="form-section">
                                  <Typography component="span" className="form-label">
                                    Flat
                                  </Typography>
                                  <TextField
                                    className="form-field"
                                    value={listItem.flat}
                                    error={addressValidation.show && addressValidation.flat?.error}
                                    helperText={
                                      addressValidation.show && addressValidation.flat?.error
                                        ? addressValidation.flat.helperText
                                        : undefined
                                    }
                                    onChange={(e) => handleAddressChange(String(listItem.id), "flat", e.target.value)}
                                  />
                                </Box>
                              </Box>
                              <Box sx={{ display: "flex", alignItems: "baseline", columnGap: "12px" }}>
                                <Box className="form-section">
                                  <Typography component="span" className="form-label">
                                    Address Detail
                                  </Typography>
                                  <TextField
                                    sx={{
                                      "& .MuiInputBase-root > textarea,& .MuiInputBase-root": {
                                        height: "auto",
                                        minHeight: "84px",
                                      },
                                    }}
                                    error={addressValidation.show && addressValidation.detail?.error}
                                    helperText={
                                      addressValidation.show && addressValidation.detail?.error
                                        ? addressValidation.detail.helperText
                                        : undefined
                                    }
                                    onChange={(e) => handleAddressChange(String(listItem.id), "detail", e.target.value)}
                                    className="form-field"
                                    multiline
                                    value={listItem.detail}
                                  />
                                </Box>
                                {driverList && driverList.length > 0 ? (
                                  <Box className="form-section">
                                    <Typography component="span" className="form-label">
                                      Delivery Driver
                                    </Typography>
                                    <Select
                                      value={listItem.driverId || listItem.driver?.id || null}
                                      onChange={(e) =>
                                        handleAddressChange(String(listItem.id), "driverId", e.target.value)
                                      }
                                    >
                                      {driverList?.map((driverItem) => (
                                        <MenuItem key={driverItem.id} value={driverItem.id}>
                                          {driverItem.fullName}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </Box>
                                ) : null}
                              </Box>
                            </Fragment>
                          );
                        })}
                    </Box>
                  </Grid>
                  <Grid item md={12} lg={5.5} sx={{ paddingTop: "20px !important" }}>
                    <Box className="form-section" mt={4}>
                      <Typography component="span" className="form-label">
                        Which Days to Go
                      </Typography>
                      <DateCalendar
                        value={calendarDeliveryDate}
                        dayOfWeekFormatter={(_day: string, date: Moment) => moment(date).format("ddd")}
                        showDaysOutsideCurrentMonth
                        disabled={deliveryCalendarDisabled}
                        views={["day"]}
                        // minDate={calendarMinDate}
                        // maxDate={calendarMaxDate}
                        onChange={handleDeliverySelection}
                        onMonthChange={handleMonthChange}
                        sx={{
                          "& .MuiPickersDay-dayOutsideMonth": {
                            // opacity: "0.25",
                          },
                          "& .MuiPickersCalendarHeader-root": {
                            backgroundColor: "#E5E7EA",
                            paddingTop: "35px",
                            paddingBottom: "20px",
                            marginBottom: 0,
                            marginTop: "4px",
                            borderTopRightRadius: "8px",
                            borderTopLeftRadius: "8px",
                            paddingLeft: "12px",
                          },
                          marginLeft: 0,
                          width: "532px",
                          maxHeight: "430px",
                          height: "430px",
                          "& .MuiPickersDay-root, & .MuiPickersDay-root:not(.Mui-selected)": {
                            borderRadius: 0,
                            borderRight: "1px solid #E5E7EA",
                            borderBottom: "1px solid #E5E7EA",
                            borderLeft: "none",
                            borderTop: "none",
                            margin: 0,
                            width: "100%",
                            position: "relative",
                          },
                          "& .MuiPickersDay-root:hover, & .MuiPickersDay-root:focus": {
                            // backgroundColor: "unset",
                          },
                          "& .MuiDayCalendar-weekContainer": {
                            margin: 0,
                          },
                          "& .MuiDayCalendar-weekDayLabel": {
                            width: "72px",
                            textTransform: "uppercase",
                            fontWeight: 600,
                            fontSize: "10px",
                          },
                          "& .MuiDayCalendar-header": {
                            backgroundColor: "#E5E7EA",
                          },
                          "& .MuiDayCalendar-slideTransition": {
                            minHeight: "400px",
                          },
                          "& .MuiDayCalendar-weekContainer > button:first-of-type": {
                            borderLeft: "1px solid #E5E7EA",
                          },
                          "& .MuiDayCalendar-monthContainer .MuiDayCalendar-weekContainer:first-of-type button": {
                            borderTop: "1px solid #E5E7EA",
                          },
                          "& .MuiDayCalendar-weekContainer:last-child button:first-of-type": {
                            borderBottomLeftRadius: "8px",
                          },
                          "& .MuiDayCalendar-weekContainer:last-child button:last-child": {
                            borderBottomRightRadius: "8px",
                          },
                        }}
                        slots={{
                          day: CustomDayDelivery,
                        }}
                        slotProps={{
                          day: {
                            addresslist: addressList,
                            deliveryinfo: deliveryInfo,
                            activeaddress: addressActiveTab,
                          } as any,
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
                <Box sx={{ display: "flex", gap: "24px" }}>
                  <Box className="form-section" sx={{ marginTop: "24px", maxWidth: "150px" }}>
                    <Can permission="USER_ADDRESS_EDIT">
                      <LoadingButton
                        sx={{ padding: "12px 24px", fontSize: "14px" }}
                        color="primary"
                        disabled={!addressList.find((i) => i.id === addressActiveTab)?.dirty}
                        loading={saveAddressLoading}
                        variant="contained"
                        disableElevation
                        onClick={saveAddressChange}
                      >
                        {saveAddressLoading ? <span>&nbsp;</span> : <span>Save Changes</span>}
                      </LoadingButton>
                    </Can>
                  </Box>
                  <Box className="form-section" sx={{ marginTop: "24px", maxWidth: "150px" }}>
                    <Button
                      sx={{
                        padding: "12px 24px",
                        fontSize: "14px",
                        color: "text.style",
                        background: "rgba(226, 64, 64, 0.08)",
                        "&:hover": { background: "rgba(226, 64, 64, 0.2)" },
                      }}
                      disabled={!addressList.find((i) => i.id === addressActiveTab)?.dirty}
                      disableElevation
                      onClick={discardAddressChange}
                    >
                      {saveAddressLoading ? <span>&nbsp;</span> : <span>Discard</span>}
                    </Button>
                  </Box>
                  <Box className="form-section" sx={{ marginTop: "24px", maxWidth: "150px" }}>
                    <Can permission="USER_ADDRESS_DELETE">
                      <LoadingButton
                        sx={{ padding: "12px 24px", fontSize: "14px" }}
                        color="error"
                        loading={saveAddressLoading}
                        variant="contained"
                        disableElevation
                        onClick={deleteAddress}
                      >
                        {saveAddressLoading ? <span>&nbsp;</span> : <span>Delete</span>}
                      </LoadingButton>
                    </Can>
                  </Box>
                </Box>
              </>
            )}
            {userDetailActiveTab === 2 && (
              <>
                <Grid container spacing={12} mt={0}>
                  <Grid item md={6} lg={4} sx={{ paddingTop: "20px !important" }}>
                    <Box py={4} sx={{ display: "flex", flexDirection: "column", rowGap: "16px" }}>
                      <Box className="form-section">
                        <Typography className="form-label-title">Allergens</Typography>
                      </Box>
                      <Box className="form-section" sx={{ rowGap: "12px" }}>
                        <TextField
                          placeholder="Add a custom allergen"
                          className="form-field"
                          value={customAllergen}
                          onChange={(e) => setCustomAllergen(e.target.value)}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Button
                                  disabled={customAllergen.trim() === ""}
                                  variant="contained"
                                  sx={{ padding: "2px 4px", fontSize: "12px" }}
                                  onClick={addCustomAllergen}
                                >
                                  Add
                                </Button>
                              </InputAdornment>
                            ),
                          }}
                        />
                        <Box className="custom-allergens" sx={{ display: "flex", columnGap: "4px", flexWrap: "wrap" }}>
                          {customAllergens.map((item) => (
                            <Button
                              sx={{ padding: "4px 8px", fontSize: "14px", lineHeight: "12px", marginBottom: "4px" }}
                              key={item}
                              variant="contained"
                              color="secondary"
                              endIcon={<DeleteIcon width={18} height={18} onClick={() => deleteCustomAllergen(item)} />}
                            >
                              {item}
                            </Button>
                          ))}
                        </Box>
                        <FormControl className="common-allergens" sx={{ width: "100%" }}>
                          <FormGroup sx={{ display: "flex", flexDirection: "row" }}>
                            {allergenList?.map((item, key) => (
                              <FormControlLabel
                                key={item}
                                className={value?.[item] === true ? "selected" : ""}
                                control={
                                  <Checkbox checked={value?.[item] === true} onChange={handleChange} name={item} />
                                }
                                label={item}
                                sx={{
                                  "&": { margin: 0, padding: "8px", flexBasis: "50%", columnGap: "4px" },
                                  "& > .MuiButtonBase-root": { padding: 0 },
                                  "& > .MuiTypography-root": {
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    color: "text.dark2",
                                  },
                                  "&.selected > .MuiTypography-root": { color: "text.dark" },
                                  fontSize: "18px",
                                  fontWeight: 600,
                                }}
                              />
                            ))}
                          </FormGroup>
                        </FormControl>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
                <Box className="form-section" sx={{ marginTop: "24px", maxWidth: "150px" }}>
                  <Can permission="USER_EDIT">
                    <LoadingButton
                      sx={{ padding: "12px 24px", fontSize: "14px" }}
                      color="primary"
                      disabled={!formDirty}
                      loading={saveLoading}
                      variant="contained"
                      disableElevation
                      onClick={saveChanges}
                    >
                      {saveLoading ? <span>&nbsp;</span> : <span>Save Changes</span>}
                    </LoadingButton>
                  </Can>
                </Box>
              </>
            )}
            {userDetailActiveTab === 3 && (
              <>
                <Grid container spacing={12} mt={0}>
                  <Grid item md={9} lg={6} sx={{ paddingTop: "20px !important" }}>
                    <Box py={4} sx={{ display: "flex", flexDirection: "column", rowGap: "16px" }}>
                      <Box className="form-section">
                        <Typography className="form-label-title">Medical Conditions</Typography>
                      </Box>
                      <Box className="form-section">
                        <FormControl className="common-allergens" sx={{ width: "100%" }}>
                          <FormGroup sx={{ display: "flex", flexDirection: "row" }}>
                            {medicalConditionList?.map((item, key) => (
                              <FormControlLabel
                                key={item}
                                className={medicalConditionSelections?.[item] === true ? "selected" : ""}
                                control={
                                  <Checkbox
                                    checked={medicalConditionSelections?.[item] === true}
                                    onChange={handleMedicalConditionChange}
                                    name={item}
                                  />
                                }
                                label={item}
                                sx={{
                                  "&": { margin: 0, padding: "8px", flexBasis: "33.33%", columnGap: "4px" },
                                  "& > .MuiButtonBase-root": { padding: 0 },
                                  "& > .MuiTypography-root": {
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    color: "text.dark2",
                                  },
                                  "&.selected > .MuiTypography-root": { color: "text.dark" },
                                  fontSize: "18px",
                                  fontWeight: 600,
                                }}
                              />
                            ))}
                          </FormGroup>
                        </FormControl>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
                <Box className="form-section" sx={{ marginTop: "24px", maxWidth: "150px" }}>
                  <Can permission="USER_EDIT">
                    <LoadingButton
                      sx={{ padding: "12px 24px", fontSize: "14px" }}
                      color="primary"
                      disabled={!formDirty}
                      loading={saveLoading}
                      variant="contained"
                      disableElevation
                      onClick={saveChanges}
                    >
                      {saveLoading ? <span>&nbsp;</span> : <span>Save Changes</span>}
                    </LoadingButton>
                  </Can>
                </Box>
              </>
            )}
            {userDetailActiveTab === 4 && (
              <>
                <Grid container spacing={12} mt={0}>
                  <Grid item md={9} lg={6} sx={{ paddingTop: "20px !important" }}>
                    <Box py={4} sx={{ display: "flex", flexDirection: "column", rowGap: "16px" }}>
                      <Box className="form-section">
                        <Typography className="form-label-title">Dislikes</Typography>
                      </Box>
                      <Box className="form-section">
                        <FormControl className="common-allergens" sx={{ width: "100%" }}>
                          <FormGroup sx={{ display: "flex", flexDirection: "row" }}>
                            {dislikeList?.map((item, key) => (
                              <FormControlLabel
                                key={item}
                                className={dislikeSelections?.[item] === true ? "selected" : ""}
                                control={
                                  <Checkbox
                                    checked={dislikeSelections?.[item] === true}
                                    onChange={handleDislikeChange}
                                    name={item}
                                  />
                                }
                                label={item}
                                sx={{
                                  "&": { margin: 0, padding: "8px", flexBasis: "33.33%", columnGap: "4px" },
                                  "& > .MuiButtonBase-root": { padding: 0 },
                                  "& > .MuiTypography-root": {
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    color: "text.dark2",
                                  },
                                  "&.selected > .MuiTypography-root": { color: "text.dark" },
                                  fontSize: "18px",
                                  fontWeight: 600,
                                }}
                              />
                            ))}
                          </FormGroup>
                        </FormControl>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
                <Box className="form-section" sx={{ marginTop: "24px", maxWidth: "150px" }}>
                  <Can permission="USER_EDIT">
                    <LoadingButton
                      sx={{ padding: "12px 24px", fontSize: "14px" }}
                      color="primary"
                      disabled={!formDirty}
                      loading={saveLoading}
                      variant="contained"
                      disableElevation
                      onClick={saveChanges}
                    >
                      {saveLoading ? <span>&nbsp;</span> : <span>Save Changes</span>}
                    </LoadingButton>
                  </Can>
                </Box>
              </>
            )}
          </Box>
        )}
        {activeTab === 1 && (
          <Box className={`user-detail-lower ${loading ? "filter" : ""}`} sx={{ flexDirection: "" }}>
            <Box className={`plan-info`}>
              <Typography className={`plan-status current`}>Active</Typography>
              <Box className="plan-container">
                {planInfo.currentPlans?.map((plan: AdminSubscriptionDetailResponse) => {
                  const lockedStart = moment(lockedStartGlobal);
                  const planActions = plan.actions?.filter(
                    (i) => i.type === "PAUSE" && (i.endDate === null || moment(i.endDate).isSameOrAfter(lockedStart))
                  ) as SubscriptionActionResponse[];
                  return (
                    <Box className={`plan-wrap`} key={plan.id}>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography className={`plan-title`}>{plan.planOption?.plan?.name} Plan</Typography>

                        {planActions.length ? (
                          <Tooltip
                            title={`Paused Dates: ${planActions
                              .map((day) =>
                                day.startDate === day.endDate
                                  ? moment(day.startDate).format(displayFormat)
                                  : moment(day.startDate).format(displayFormat) +
                                    "-" +
                                    (day.endDate ? moment(day.endDate).format(displayFormat) : "Continuous")
                              )
                              .join(", ")}`}
                            placement="left"
                            arrow
                          >
                            <Box className={`plan-suspension`}>
                              <Typography sx={{ fontSize: "12px", fontWeight: 500, lineHeight: "normal" }}>
                                Paused
                              </Typography>

                              <Tooltip title="Edit Dates" placement="top" arrow>
                                <Can permission="USER_SUBSCRIPTION_EDIT">
                                  <Edit2Icon onClick={() => handleEditPanelOpen(plan)} />
                                </Can>
                              </Tooltip>
                              {/* <Tooltip title="Delete All Paused Dates" placement="top" arrow>
                                <Delete2Icon width={18} height={19} onClick={() => deleteAllActions()} />
                              </Tooltip> */}
                            </Box>
                          </Tooltip>
                        ) : null}
                      </Box>

                      <Box className={`plan-details`}>
                        <Typography className="plan-option">{plan.planOption?.name}</Typography>
                        <Typography className="seperator">|</Typography>
                        <Typography className="plan-name">{plan.planOption?.selection}</Typography>
                        <Typography className="seperator">|</Typography>
                        <Typography className={`plan-days current`}>{plan.remainingDays} Deliveries Left</Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                          borderBottomStyle: "solid",
                          borderBottomWidth: "1px",
                          borderBottomColor: "text.light3",
                          padding: "12px 0",
                        }}
                      >
                        <Typography className={`plan-date`}>
                          Start Date: {moment(plan.startDate).format(displayFormat)} - End Date:{" "}
                          {plan.endDate ? moment(plan.endDate).format(displayFormat) : "Continuous"}
                        </Typography>
                        <DeliveryDays deliveryDays={plan.deliveryDays} />
                      </Box>

                      <DietaryTargets dietaryTargets={plan.dietaryTargets} />
                      <Box className={`plan-sub`}>
                        <Box sx={{ display: "flex", gap: "6px", alignItems: "center" }}>
                          {plan.cancellable && (
                            <Can permission="USER_SUBSCRIPTION_DELETE">
                              <Button
                                onClick={() => handleCancelSubscription(plan.id, plan.balanceType)}
                                size="small"
                                className="stop blend-gray"
                                startIcon={<CloseIconCircle width={15} height={15} />}
                              >
                                Cancel
                              </Button>
                            </Can>
                          )}
                          {!planActions.length && (
                            <Can permission="USER_SUBSCRIPTION_EDIT">
                              <Button
                                onClick={() => handleStopPanelOpen(plan)}
                                size="small"
                                className="stop blend-gray"
                                startIcon={<PauseIcon width={15} height={15} />}
                              >
                                Pause
                              </Button>
                            </Can>
                          )}
                          {/* <Button
                            onClick={() => handleResumePanelOpen(plan)}
                            size="small"
                            className="resume blend-green"
                            startIcon={<StartIcon width={15} height={15} />}
                          >
                            Resume
                          </Button> */}
                          <Can permission="USER_SUBSCRIPTION_EDIT">
                            <Button
                              onClick={() => handleEditPlan(plan)}
                              className="edit blend-gray"
                              size="small"
                              startIcon={<Edit3Icon width={13} height={13} />}
                            >
                              Edit Plan
                            </Button>
                          </Can>
                        </Box>
                        {checkIf(plan.invoiceId) && (
                          <LoadingButton
                            onClick={() => downloadInvoice(plan)}
                            className="invoice blend-blue"
                            loading={invoiceDownloadProcess}
                            size="small"
                            startIcon={!invoiceDownloadProcess && <DownloadIcon width={14} height={15} />}
                          >
                            {invoiceDownloadProcess ? <span>&nbsp;</span> : <span>Download Invoice</span>}
                          </LoadingButton>
                        )}
                      </Box>
                    </Box>
                  );
                })}
                <Box
                  className={`add-new-plan-wrap`}
                  sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 133 }}
                >
                  <Can permission="USER_SUBSCRIPTION_CREATE">
                    <LoadingButton
                      color="primary"
                      variant="contained"
                      // loading={addNewMenuLoading}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        marginBottom: "16px",
                        maxWidth: 200,
                        padding: "8px 24px",
                      }}
                      onClick={handleOpenSubscriptionCrud}
                    >
                      <PlusIcon />
                      <Typography sx={{ fontSize: 14 }}>Add New Plan</Typography>
                    </LoadingButton>
                  </Can>
                  <Typography className="add-new-plan-info">Please click to add a new plan to the user</Typography>

                  <SubscriptionCrud
                    addressList={addressListRef}
                    userId={user?.id}
                    create
                    open={subscriptionCrudOpen}
                    onClose={handleSubscriptionClose}
                    onConfirm={handleSubscriptionConfirm}
                    title="Add New Subscription"
                  />
                  <SubscriptionCrud
                    addressList={addressListRef}
                    userId={user?.id}
                    edit
                    plan={planToEdit}
                    open={subscriptionEditOpen}
                    onClose={handleSubscriptionClose}
                    onConfirm={handleSubscriptionEdit}
                    title="Edit Subscription"
                  />
                </Box>
              </Box>
            </Box>
            {planInfo.futurePlans?.length ? (
              <Box className={`plan-info`}>
                <Typography className={`plan-status future`}>Future</Typography>
                <Box className="plan-container">
                  {planInfo.futurePlans?.map((plan: AdminSubscriptionDetailResponse) => {
                    const lockedStart = moment(lockedStartGlobal);
                    const planActions = plan.actions?.filter(
                      (i) => i.type === "PAUSE" && (i.endDate === null || moment(i.endDate).isSameOrAfter(lockedStart))
                    ) as SubscriptionActionResponse[];
                    return (
                      <Box className={`plan-wrap`} key={plan.id}>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography className={`plan-title`}>{plan.planOption?.plan?.name} Plan</Typography>
                          {planActions.length ? (
                            <Tooltip
                              title={`Paused Dates: ${planActions
                                .map((day) =>
                                  day.startDate === day.endDate
                                    ? moment(day.startDate).format(displayFormat)
                                    : moment(day.startDate).format(displayFormat) +
                                      "-" +
                                      (day.endDate ? moment(day.endDate).format(displayFormat) : "Continuous")
                                )
                                .join(", ")}`}
                              placement="left"
                              arrow
                            >
                              <Box className={`plan-suspension`}>
                                <Typography sx={{ fontSize: "12px", fontWeight: 500, lineHeight: "normal" }}>
                                  Paused
                                </Typography>
                                <Tooltip title="Edit Dates" placement="bottom" arrow>
                                  <Can permission="USER_SUBSCRIPTION_EDIT">
                                    <Edit2Icon onClick={() => handleEditPanelOpen(plan)} />
                                  </Can>
                                </Tooltip>
                                {/* <Tooltip title="Delete All Paused Dates" placement="top" arrow>
                                  <Delete2Icon width={18} height={19} onClick={() => deleteAllActions()} />
                                </Tooltip> */}
                              </Box>
                            </Tooltip>
                          ) : null}
                        </Box>
                        <Box className={`plan-details`}>
                          <Typography className="plan-option">{plan.planOption?.name}</Typography>
                          <Typography className="seperator">|</Typography>
                          <Typography className="plan-name">{plan.planOption?.selection}</Typography>
                          <Typography className="seperator">|</Typography>
                          <Typography className={`plan-days future`}>
                            {Math.abs(plan.daysToStart || 0)} Days to Start
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                            borderBottomStyle: "solid",
                            borderBottomWidth: "1px",
                            borderBottomColor: "text.light3",
                            padding: "12px 0",
                          }}
                        >
                          <Typography className={`plan-date`}>
                            Start Date: {moment(plan.startDate).format(displayFormat)} - End Date:{" "}
                            {plan.endDate ? moment(plan.endDate).format(displayFormat) : "Continuous"}
                          </Typography>
                          <DeliveryDays deliveryDays={plan.deliveryDays} />
                        </Box>

                        <DietaryTargets dietaryTargets={plan.dietaryTargets} />
                        <Box className={`plan-sub`}>
                          <Box sx={{ display: "flex", gap: "6px", alignItems: "center" }}>
                            {plan.cancellable && (
                              <Can permission="USER_SUBSCRIPTION_DELETE">
                                <Button
                                  onClick={() => handleCancelSubscription(plan.id, plan.balanceType)}
                                  size="small"
                                  className="stop blend-gray"
                                  startIcon={<CloseIconCircle width={15} height={15} />}
                                >
                                  Cancel
                                </Button>
                              </Can>
                            )}
                            {!planActions.length && (
                              <Can permission="USER_SUBSCRIPTION_EDIT">
                                <Button
                                  onClick={() => handleStopPanelOpen(plan)}
                                  size="small"
                                  className="stop blend-gray"
                                  startIcon={<PauseIcon width={15} height={15} />}
                                >
                                  Pause
                                </Button>
                              </Can>
                            )}
                            {/* <Button
                              onClick={() => handleResumePanelOpen(plan)}
                              size="small"
                              className="resume blend-green"
                              startIcon={<StartIcon width={15} height={15} />}
                            >
                              Resume
                            </Button> */}
                            <Button
                              onClick={() => handleEditPlan(plan)}
                              className="edit blend-gray"
                              size="small"
                              startIcon={<Edit3Icon width={13} height={13} />}
                            >
                              Edit Plan
                            </Button>
                          </Box>
                          {checkIf(plan.invoiceId) && (
                            <LoadingButton
                              onClick={() => downloadInvoice(plan)}
                              className="invoice blend-blue"
                              loading={invoiceDownloadProcess}
                              size="small"
                              startIcon={!invoiceDownloadProcess && <DownloadIcon width={14} height={15} />}
                            >
                              {invoiceDownloadProcess ? <span>&nbsp;</span> : <span>Download Invoice</span>}
                            </LoadingButton>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ) : null}
            {planInfo.pastPlans?.length ? (
              <Box className={`plan-info`}>
                <Typography className={`plan-status past`}>Past</Typography>
                <Box className="plan-container">
                  {planInfo.pastPlans?.map((plan: AdminSubscriptionDetailResponse) => (
                    <Box className={`plan-wrap`} key={plan.id}>
                      <Typography className={`plan-title`}>{plan.planOption?.plan?.name} Plan</Typography>
                      <Box className={`plan-details`}>
                        <Typography className="plan-option">{plan.planOption?.name}</Typography>
                        <Typography className="seperator">|</Typography>
                        <Typography className="plan-name">{plan.planOption?.selection}</Typography>
                        <Typography className="seperator">|</Typography>
                        <Typography className={`plan-days past`}>Plan Ended</Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                          borderBottomStyle: "solid",
                          borderBottomWidth: "1px",
                          borderBottomColor: "text.light3",
                          padding: "12px 0",
                        }}
                      >
                        <Typography className={`plan-date`}>
                          Start Date: {moment(plan.startDate).format(displayFormat)} - End Date:{" "}
                          {moment(plan.endDate).format(displayFormat)}
                        </Typography>
                        <DeliveryDays deliveryDays={plan.deliveryDays} />
                      </Box>

                      <DietaryTargets dietaryTargets={plan.dietaryTargets} />
                      <Box className={`plan-sub`}>
                        <Typography className={`plan-price`}></Typography>
                        {checkIf(plan.invoiceId) && (
                          <LoadingButton
                            onClick={() => downloadInvoice(plan)}
                            className="invoice blend-blue"
                            loading={invoiceDownloadProcess}
                            size="small"
                            startIcon={!invoiceDownloadProcess && <DownloadIcon width={14} height={15} />}
                          >
                            {invoiceDownloadProcess ? <span>&nbsp;</span> : <span>Download Invoice</span>}
                          </LoadingButton>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : null}
            {planInfo.cancelledPlans?.length ? (
              <Box className={`plan-info`}>
                <Typography className={`plan-status cancelled`}>Cancelled</Typography>
                <Box className="plan-container">
                  {planInfo.cancelledPlans?.map((plan: AdminSubscriptionDetailResponse) => (
                    <Box className={`plan-wrap`} key={plan.id}>
                      <Typography className={`plan-title`}>{plan.planOption?.plan?.name} Plan</Typography>

                      <Box className={`plan-details`}>
                        <Typography className="plan-option">{plan.planOption?.name}</Typography>
                        <Typography className="seperator">|</Typography>
                        <Typography className="plan-name">{plan.planOption?.selection}</Typography>
                        <Typography className="seperator">|</Typography>
                        <Typography className={`plan-days past`}>{plan.remainingDays} Deliveries Left</Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                          borderBottomStyle: "solid",
                          borderBottomWidth: "1px",
                          borderBottomColor: "text.light3",
                          padding: "12px 0",
                        }}
                      >
                        <Typography className={`plan-date`}>
                          Start Date: {moment(plan.startDate).format(displayFormat)} - End Date:{" "}
                          {moment(plan.endDate).format(displayFormat)} | Cancelled On:{" "}
                          {moment(plan.cancellationDate).format(displayFormat)}
                        </Typography>
                        <DeliveryDays deliveryDays={plan.deliveryDays} />
                      </Box>
                      <DietaryTargets dietaryTargets={plan.dietaryTargets} />
                      <Box className={`plan-sub`}>
                        <Typography className={`plan-price`}></Typography>
                        {checkIf(plan.invoiceId) && (
                          <LoadingButton
                            onClick={() => downloadInvoice(plan)}
                            className="invoice blend-blue"
                            loading={invoiceDownloadProcess}
                            size="small"
                            startIcon={!invoiceDownloadProcess && <DownloadIcon width={14} height={15} />}
                          >
                            {invoiceDownloadProcess ? <span>&nbsp;</span> : <span>Download Invoice</span>}
                          </LoadingButton>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : null}
          </Box>
        )}
        {activeTab === 2 && (
          <Box className="meal-planning">
            <Box
              className="plan-inner"
              sx={{ padding: "12px 0 !important", rowGap: "12px !important", boxShadow: "none !important" }}
            >
              <Box className="calendar-wrapper">
                <Box onClick={handlePreviousDay} className="icon-container">
                  <ArrowLeftIcon />
                </Box>

                <Box className="days-wrapper">
                  {currentDateFrame.map((frameItem) => {
                    const formattedDate = frameItem.format(defaultFormat);
                    const active = formattedDate === selectedDate ? "active" : "";
                    const found = selectableDates.find((i) => i.date === formattedDate && i.status === "UNSELECTABLE");
                    const paused = selectableDates.find((i) => i.date === formattedDate && i.status === "PAUSED");
                    const unselectableClass = Boolean(found) ? "unselectable" : "";
                    return (
                      <Box
                        className={`day ${active} ${unselectableClass}`}
                        key={formattedDate}
                        onClick={(e) => (Boolean(found) ? e.preventDefault() : updateSelectedDate(formattedDate))}
                      >
                        <Typography component={"span"} className="day-of-month">
                          {frameItem.format("D")}
                        </Typography>
                        <Typography component={"span"} className="name-of-month">
                          {frameItem.format("MMM")}
                        </Typography>
                        <Typography component={"span"} className="name-of-day">
                          {frameItem.format("dddd")}
                        </Typography>
                        {paused && (
                          <Tooltip title="Paused" placement="top" arrow>
                            <Box sx={{ position: "absolute", right: "4px", top: "4px" }}>
                              <PauseIcon color={theme.palette.text.orange} />
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    );
                  })}
                </Box>
                <Box onClick={handleNextDay} className="icon-container">
                  <ArrowRightIcon />
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "32px" }}>
                <FormControlLabel
                  sx={{
                    margin: 0,
                    columnGap: "8px",
                    "& .MuiFormControlLabel-label": { fontSize: "14px", fontWeight: 500 },
                  }}
                  control={
                    <Toggle checked={hideMealTypes} onChange={(e) => setHideMealTypes(Boolean(e.target.checked))} />
                  }
                  label="Hide Empty Meal Types"
                />
                <Box>
                  <Can permission="REVIEW_CREATE">
                    <LoadingButton
                      loading={updateStatusLoading}
                      disabled={moment().isAfter(moment(selectedDate)) || selectionMode.status === "UNSELECTABLE"}
                      color={userMenu.status === "PENDING_REVIEW" ? "warning" : "primary"}
                      size="medium"
                      variant="contained"
                      sx={{ height: 50 }}
                      startIcon={userMenu.status === "PENDING_REVIEW" ? <ReviewWarningIcon /> : <CheckIcon />}
                      onClick={updateStatus}
                    >
                      {updateStatusLoading ? (
                        <span>&nbsp;</span>
                      ) : userMenu.status === "PENDING_REVIEW" ? (
                        <span>Mark as Reviewed</span>
                      ) : (
                        <span>Reviewed</span>
                      )}
                    </LoadingButton>
                  </Can>
                </Box>
              </Box>
              <Box className="table-wrapper" sx={{ background: "none !important", border: "none !important" }}>
                {globalLoading && <MainSpinner />}
                <>
                  {mealTypes?.map((mealType) => {
                    const hidden = hideMealTypes && !Boolean(userSelections.find((i) => i.mealType === mealType.name));
                    return !hidden ? (
                      <Accordion
                        key={mealType.name}
                        expanded={
                          checkIf(mealTypePanelState[String(mealType.name)])
                            ? Boolean(mealTypePanelState[String(mealType.name)])
                            : true
                        }
                        onChange={handlePanelExpandChange(String(mealType.name))}
                        sx={{
                          boxShadow: "none",
                          "&,&.Mui-expanded": { margin: "0 0 12px 0" },
                          "&:before": { opacity: 0 },
                          "&:last-of-type": { marginBottom: 0 },
                        }}
                      >
                        <AccordionSummary
                          sx={{
                            "&, &.Mui-expanded": { minHeight: "auto" },
                            padding: "0 12px",
                            backgroundColor: "text.light3",
                            "& .MuiAccordionSummary-content": { margin: 0, padding: "12px 0" },
                            "& .MuiAccordionSummary-content.Mui-expanded": { margin: 0 },
                          }}
                          expandIcon={<ArrowDropDown />}
                        >
                          <Typography
                            sx={{ color: "text.dark", fontSize: "12px", fontWeight: 600, lineHeight: "14px" }}
                          >
                            {mealType.description}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ padding: 0 }}>
                          {userSelections
                            .filter((userSelection) => userSelection.mealType === mealType.name)
                            .map((selection, _, array) => {
                              const targetSelection = selectionCache.find((i) => i.selectionId === selection.id);

                              const cachedPortion = targetSelection?.selectedPortion;
                              const cachedType = targetSelection?.portionType;

                              let selectedPortion: (AUMSMealPortionResponse & AUMSSelectionPortionResponse) | undefined;

                              if (cachedType === AUMSSelectionPortionResponseTypeEnum.Default) {
                                selectedPortion = {
                                  ...selection.meal?.portions?.find(
                                    (i: AMMLMealPortionResponse) => i.portion === cachedPortion
                                  ),
                                  type: cachedType,
                                };
                              } else {
                                selectedPortion = { ...selection.portion };
                              }

                              const isCustomOption =
                                selection.portion?.type !== AUMSSelectionPortionResponseTypeEnum.Default;

                              const selectionCacheForMealType = selectionCache.filter((i) =>
                                array.some((item) => item.id === i.selectionId)
                              );

                              const approvedSelectionLength = selectionCacheForMealType.filter(
                                (i) => i.added === true
                              ).length;
                              const nonApprovedSelectionLength = selectionCacheForMealType.filter(
                                (i) => i.added === false
                              ).length;
                              const noCustomSelection = selectionCacheForMealType.every(
                                (i) => typeof i.added === "undefined"
                              );
                              const selfNotAdded =
                                selectionCacheForMealType.find((i) => i.selectionId === selection.id)?.added === false;
                              const deleteMode =
                                (array.length > 1 && noCustomSelection) || approvedSelectionLength || selfNotAdded;
                              return (
                                <Fragment key={selection.id}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "stretch",
                                      border: "1px solid",
                                      borderColor: "text.light3",
                                      position: "relative",
                                      borderTop: "none",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        rowGap: "8px",
                                        padding: "12px 16px",
                                        borderRight: "1px solid",
                                        borderColor: "text.light3",
                                        flex: "2 2",
                                        justifyContent: "center",
                                        position: "relative",
                                      }}
                                    >
                                      <Typography sx={{ color: "text.dark", fontSize: "14px", fontWeight: 500 }}>
                                        {selection.meal?.name}
                                      </Typography>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          columnGap: "4px",
                                          "& > span": {
                                            whiteSpace: "nowrap",
                                            padding: "4px 8px",
                                            borderRadius: "4px",
                                            backgroundColor: "text.tableColor",
                                            color: "text.dark",
                                            fontSize: "10px",
                                            lineHeight: "13px",
                                            fontWeight: 500,
                                          },
                                          "& .MuiButton-root": {
                                            display: "flex",
                                            gap: "4px",
                                            padding: "4px 6px",
                                            borderRadius: "4px",
                                            height: "21px",
                                            minWidth: "auto",
                                          },
                                        }}
                                      >
                                        <Typography component={"span"}>Cal: {selectedPortion?.calories}</Typography>
                                        <Typography component={"span"}>Pro: {selectedPortion?.protein}gr</Typography>
                                        <Typography component={"span"}>Carbs: {selectedPortion?.carbs}gr</Typography>
                                        <Typography component={"span"}>Fat: {selectedPortion?.fat}gr</Typography>
                                        {selection.customPortionRequest &&
                                        selection.customPortionRequest.status ===
                                          AUMSCustomPortionRequestResponseStatusEnum.Pending ? (
                                          <Button
                                            onClick={() => {
                                              setSelection(selection);
                                              setPortionCustomizeOpen(true);
                                            }}
                                            color="warning"
                                            variant="contained"
                                          >
                                            <Edit2Icon width={14} height={14} />
                                            <Typography
                                              sx={{ fontSize: "10px", fontWeight: 600, lineHeight: "normal" }}
                                            >
                                              Request
                                            </Typography>
                                          </Button>
                                        ) : targetSelection?.added !== false ? (
                                          <Button
                                            onClick={() => {
                                              setSelection(selection);
                                              setPortionCustomizeOpen(true);
                                            }}
                                            color="secondary"
                                            variant="contained"
                                          >
                                            <Edit2Icon width={14} height={14} />
                                          </Button>
                                        ) : null}
                                        {selection.customPortionRequest &&
                                        (selection.customPortionRequest.status ===
                                          AUMSCustomPortionRequestResponseStatusEnum.Rejected ||
                                          selection.customPortionRequest.status ===
                                            AUMSCustomPortionRequestResponseStatusEnum.Cancelled) ? (
                                          <Tooltip
                                            title={
                                              <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                                                <Typography fontSize={12} fontWeight={500}>
                                                  Previously {translateStatus(selection.customPortionRequest.status)}{" "}
                                                  with values:
                                                </Typography>
                                                <Box sx={{ display: "flex", gap: "4px" }}>
                                                  <Typography fontSize={12} fontWeight={500} component={"span"}>
                                                    Cal:{selection.customPortionRequest?.calories}
                                                  </Typography>
                                                  <Typography fontSize={12} fontWeight={500} component={"span"}>
                                                    Pro:{selection.customPortionRequest?.protein}g
                                                  </Typography>
                                                  <Typography fontSize={12} fontWeight={500} component={"span"}>
                                                    Carbs:{selection.customPortionRequest?.carbs}g
                                                  </Typography>
                                                  <Typography fontSize={12} fontWeight={500} component={"span"}>
                                                    Fat:{selection.customPortionRequest?.fat}g
                                                  </Typography>
                                                </Box>
                                              </Box>
                                            }
                                            arrow
                                          >
                                            <Typography
                                              color={"secondary"}
                                              sx={{ display: "flex", alignItems: "center" }}
                                            >
                                              <InfoIcon />
                                            </Typography>
                                          </Tooltip>
                                        ) : null}
                                      </Box>

                                      {selection.selectorRole === "SYSTEM" && (
                                        <Tooltip title="Added by System" placement="left" arrow>
                                          <Box sx={{ position: "absolute", right: "8px", top: "8px" }}>
                                            <ChipIcon color={theme.palette.text.gray2} />
                                          </Box>
                                        </Tooltip>
                                      )}
                                      {selection.selectorRole === "ADMIN" && (
                                        <Tooltip title="Added by Admin" placement="left" arrow>
                                          <Box sx={{ position: "absolute", right: "8px", top: "8px" }}>
                                            <AdminIcon color={theme.palette.primary.main} />
                                          </Box>
                                        </Tooltip>
                                      )}
                                      {selection.selectorRole === "USER" && (
                                        <Tooltip title="Added by User" placement="left" arrow>
                                          <Box sx={{ position: "absolute", right: "8px", top: "8px" }}>
                                            <UserIcon color={theme.palette.text.orange} />
                                          </Box>
                                        </Tooltip>
                                      )}
                                    </Box>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        rowGap: "4px",
                                        padding: "12px 16px",
                                        borderRight: "1px solid",
                                        borderColor: "text.light3",
                                        flex: "1 1",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          color: "text.dark2",
                                          fontSize: "10px",
                                          lineHeight: "13px",
                                          fontWeight: 500,
                                        }}
                                      >
                                        Portion
                                      </Typography>
                                      <FormControl sx={{ minWidth: "110px" }}>
                                        <Can permission="USER_MEAL_PLAN_EDIT">
                                          <Select
                                            sx={{
                                              margin: 0,
                                              padding: "4px 0px",
                                              backgroundColor: "text.tableColor",
                                              borderColor: "text.tableColor",
                                              height: "32px",
                                              textTransform: "lowercase",
                                              fontSize: "12px",
                                              fontWeight: 500,
                                              "&.Mui-readOnly *": { cursor: "not-allowed" },
                                            }}
                                            readOnly={selectionMode.state}
                                            value={`${selectedPortion.portion} ${selectedPortion.type}`}
                                            onChange={(e) => handlePortionChange(selection, e)}
                                          >
                                            {selection.meal?.portions?.map((p: AUMSMealPortionResponse) => {
                                              return (
                                                <MenuItem
                                                  sx={{ textTransform: "lowercase" }}
                                                  key={p.portion}
                                                  value={`${p.portion} DEFAULT`}
                                                >
                                                  {p.portion} {selection.meal?.portionUnit}
                                                </MenuItem>
                                              );
                                            })}
                                            {isCustomOption && (
                                              <MenuItem
                                                sx={{ textTransform: "lowercase" }}
                                                key={"adjusted"}
                                                value={`${selection.portion?.portion} ${selection.portion?.type}`}
                                              >
                                                {selection.portion?.portion} {selection.meal?.portionUnit} - Custom
                                              </MenuItem>
                                            )}
                                          </Select>
                                        </Can>
                                      </FormControl>
                                    </Box>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        rowGap: "4px",
                                        padding: "12px 16px",
                                        borderRight: "1px solid",
                                        borderColor: "text.light3",
                                        flex: "2 2",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <FormControl
                                        sx={{
                                          width: "100%",
                                          "& .MuiFormControlLabel-label": {
                                            fontSize: "14px",
                                            lineHeight: "normal",
                                          },
                                          "& .selected .MuiFormControlLabel-label": {
                                            textDecoration: "line-through",
                                            color: "text.dark",
                                          },
                                          "& .unselected .MuiFormControlLabel-label": {
                                            fontWeight: 400,
                                            color: "text.dark2",
                                          },
                                          "& .MuiFormGroup-root": {
                                            flexDirection: "row",
                                          },
                                          "& .MuiFormControlLabel-root .MuiFormControlLabel-label.Mui-disabled": {
                                            color: "rgba(0,0,0,0.2)",
                                          },
                                          "& .MuiCheckbox-root": {
                                            padding: "4px",
                                          },
                                        }}
                                      >
                                        <FormGroup>
                                          {(selection.meal?.ingredients as IMealIngredientResponse[])
                                            ?.filter((i) => !i.removable)
                                            .map((option) => (
                                              <FormControlLabel
                                                key={option.id}
                                                control={
                                                  <Checkbox
                                                    disabled={true}
                                                    name={option.id}
                                                    icon={<SquareIcon color={"rgba(0,0,0,0.2)"} />}
                                                  />
                                                }
                                                label={option.name}
                                              />
                                            ))}
                                        </FormGroup>
                                        <FormGroup>
                                          {(selection.meal?.ingredients as IMealIngredientResponse[])
                                            ?.filter((i) => i.removable)
                                            .map((option) => {
                                              const optionSelected = selectionCache
                                                .find((i) => i.selectionId === selection.id)
                                                ?.removedIngredients?.includes(String(option.id));
                                              return (
                                                <Can permission="USER_MEAL_PLAN_EDIT" key={option.id}>
                                                  <FormControlLabel
                                                    key={option.id}
                                                    className={optionSelected ? "selected" : "unselected"}
                                                    control={
                                                      <Checkbox
                                                        checked={optionSelected}
                                                        disabled={selectionMode.state}
                                                        onChange={(e, checked) =>
                                                          onIngredientChange(selection, String(option.id), checked)
                                                        }
                                                        name={option.id}
                                                        icon={<SquareIcon color={theme.palette.text.fontLight2} />}
                                                        checkedIcon={<SquareMinusIcon color="#000" />}
                                                      />
                                                    }
                                                    label={option.name}
                                                  />
                                                </Can>
                                              );
                                            })}
                                        </FormGroup>
                                      </FormControl>
                                    </Box>
                                    <Box
                                      sx={{
                                        display: "inline-flex",
                                        alignItems: "flex-start",
                                        columnGap: "4px",
                                        padding: "9px 150px 9px 12px",
                                        flex: "2 2",
                                        borderColor: "text.light3",
                                      }}
                                    >
                                      <DraftIcon color="text.fontLight3" />
                                      <Can permission="USER_MEAL_PLAN_EDIT">
                                        <TextField
                                          sx={{
                                            "& .MuiInputBase-root > textarea,& .MuiInputBase-root": {
                                              height: "auto",
                                              minHeight: "56px",
                                              margin: 0,
                                              fontSize: "14px",
                                              border: "none",
                                              padding: 0,
                                            },
                                            "& fieldset": { border: "none" },
                                            "& .Mui-readOnly *": { cursor: "not-allowed" },
                                            color: "text.dark",
                                            fontSize: "14px",
                                            fontWeight: 500,
                                            flex: "1 0",
                                          }}
                                          InputProps={{
                                            readOnly: selectionMode.state,
                                          }}
                                          placeholder="Add notes"
                                          onChange={(e) => handleDietitianNoteChange(selection, e.target.value)}
                                          className="form-field"
                                          multiline
                                          value={
                                            selectionCache.find((i) => i.selectionId === selection.id)?.dietitianNote
                                          }
                                        />
                                      </Can>
                                    </Box>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        gap: "4px",
                                        flex: "0 0",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        padding: "0 12px",
                                        position: "absolute",
                                        right: 0,
                                        top: 0,
                                        bottom: 0,
                                      }}
                                    >
                                      {selectionCache.find((i) => i.selectionId === selection.id)?.updated ? (
                                        <Can permission="USER_MEAL_PLAN_EDIT">
                                          <Button
                                            onClick={() => handleSaveChanges(selection)}
                                            color="primary"
                                            variant="contained"
                                            sx={{ padding: "5px 8px", fontSize: "12px" }}
                                          >
                                            Save Changes
                                          </Button>
                                        </Can>
                                      ) : null}
                                      {deleteMode && !selectionMode.state && (
                                        <Can permission="USER_MEAL_PLAN_DELETE">
                                          <IconButton
                                            onClick={() => handleDeleteMeal(selection)}
                                            sx={{
                                              color: "text.style",
                                              borderRadius: "4px",
                                              background: "rgba(226, 64, 64, 0.08)",
                                              padding: "8px",
                                              width: "32px",
                                              height: "32px",
                                              "&:hover": { background: "rgba(226, 64, 64, 0.2)" },
                                            }}
                                          >
                                            <Delete3Icon width={18} height={18} />
                                          </IconButton>
                                        </Can>
                                      )}
                                    </Box>
                                    {selectionCache.find((i) => i.selectionId === selection.id)?.process && (
                                      <MainSpinner />
                                    )}
                                  </Box>
                                </Fragment>
                              );
                            })}
                          {!selectionMode.state && (
                            <Box width={200} mt="4px">
                              <Can permission="USER_MEAL_PLAN_CREATE">
                                <TextSearch meals={meals} onChange={(...args) => mealSelection(...args, mealType)} />
                              </Can>
                            </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    ) : null;
                  })}
                </>
              </Box>
              {!globalLoading && (
                <Box
                  className="target-calorie-wrapper"
                  sx={{
                    padding: 0,
                    display: "flex",
                    columnGap: "16px",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  {/* <Box sx={{ display: "inline-flex", alignItems: "center", columnGap: "4px" }}>
                    <Typography sx={{ fontSize: "16px", fontWeight: 500, color: "text.dark", whiteSpace: "nowrap" }}>
                      Target Calorie:
                    </Typography>
                    <TextField
                      inputRef={inputRef}
                      sx={{
                        color: "text.dark",
                        padding: 0,
                        width: "120px",
                        margin: 0,
                        "& .MuiInputBase-root": {
                          margin: 0,
                          height: "auto",
                          padding: "9px 16px",
                        },
                        "& .MuiInputBase-input": {
                          fontSize: "16px",
                          fontWeight: 700,
                          lineHeight: "11px",
                          padding: 0,
                        },
                      }}
                      className="text-input"
                      value={targetCalorie?.value}
                      onChange={targetCalorieValueChange}
                      InputProps={{
                        readOnly: targetCalorie?.process,
                        endAdornment: (
                          <InputAdornment position="end">
                            {targetCalorie?.process ? (
                              <Box
                                sx={{
                                  position: "absolute",
                                  width: "20px",
                                  height: "30px",
                                  right: "10px",
                                  "& span": { width: "20px", height: "20px" },
                                  "& span:after": { display: "none" },
                                }}
                              >
                                <MainSpinner />
                              </Box>
                            ) : (
                              <Tooltip
                                title={targetCalorie?.editMode ? "Save Changes" : "Edit Value"}
                                placement="top"
                                arrow
                              >
                                <IconButton edge="end">
                                  {targetCalorie?.editMode ? (
                                    <CheckIcon color="#37bd6b" width={20} height={20} onClick={handleSaveModeClick} />
                                  ) : (
                                    <Edit2Icon onClick={handleEditModeClick} />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box> */}
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      columnGap: "4px",
                      padding: "12px 16px",
                      background: "rgba(255, 143, 11, 0.03)",
                      border: "1px solid rgba(255, 143, 11, 0.10)",
                      borderRadius: "8px",
                      height: "43px",
                      flex: "1 0",
                    }}
                  >
                    <Box sx={{ display: "inline-flex", alignItems: "center", columnGap: "16px" }}>
                      <Box sx={{ display: "flex", flexWrap: "nowrap" }}>
                        <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "text.dark" }}>Protein:</Typography>
                        <Typography sx={{ fontSize: "16px", fontWeight: 500, color: "text.dark" }}>
                          {calculation.protein} gr
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", flexWrap: "nowrap" }}>
                        <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "text.dark" }}>Carbs:</Typography>
                        <Typography sx={{ fontSize: "16px", fontWeight: 500, color: "text.dark" }}>
                          {calculation.carbs} gr
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", flexWrap: "nowrap" }}>
                        <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "text.dark" }}>Fat:</Typography>
                        <Typography sx={{ fontSize: "16px", fontWeight: 500, color: "text.dark" }}>
                          {calculation.fat} gr
                        </Typography>
                      </Box>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#EE8100" }}>
                        Total: {calculation.cal} Calories
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}
        {activeTab === 3 && (
          <Box className={`user-detail-lower ${loading ? "filter" : ""}`} sx={{ flexDirection: "" }}>
            <Box sx={{ display: "flex", alignItems: "center", margin: "12px 0", justifyContent: "space-between" }}>
              <Box className="payment-history-balance">
                <Typography component={"span"}>Balance</Typography>
                <Typography>{formatCurrency(userReference?.account?.totalBalance)}</Typography>
              </Box>
              <Box className="payment-history-actions">
                <Can permission="USER_PAYMENT_HISTORY_CREATE">
                  <Button
                    onClick={() => setTransactionCrudOpen({ show: true, type: "deposit", edit: undefined })}
                    color="primary"
                    variant="contained"
                    sx={{ padding: "12px 16px", fontSize: "14px" }}
                  >
                    Deposit
                  </Button>
                </Can>
                <Can permission="USER_PAYMENT_HISTORY_CREATE">
                  <Button
                    onClick={() => setTransactionCrudOpen({ show: true, type: "withdraw", edit: undefined })}
                    sx={{
                      "&:hover": { backgroundColor: "text.light3" },
                      backgroundColor: "text.tableColor",
                      fontSize: "14px",
                      fontWeight: 500,
                      lineHeight: "normal",
                      display: "flex",
                      padding: "12px 16px",
                      "&, & svg": { color: "text.dark2" },
                    }}
                  >
                    Withdraw
                  </Button>
                </Can>
              </Box>
            </Box>
            <DataTable
              rows={paymentHistory}
              columns={columns}
              loading={loading}
              paginationMode="client"
              paginationModel={paginationModel}
              paginationChange={handlePaginationChange}
              edit={editPaymentHistoryRecord}
              deleteRec={deletePaymentHistoryRecord}
              options={{
                hideToolbar: true,
                entityName: "payment",
                permission: {
                  create: "USER_PAYMENT_HISTORY_CREATE",
                  edit: "USER_PAYMENT_HISTORY_EDIT",
                  delete: "USER_PAYMENT_HISTORY_DELETE",
                },
              }}
            />
          </Box>
        )}
      </Box>
      <ConfirmDialog />
      <ConfirmDeleteAddressDialog />
      <AddressTabChangeDialog />
      <PortionChangeDialog />
      <ConfirmSendPasswordDialog />
      <ConfirmTransactionDeleteDialog />
      <SubscriptionCancelDialog />
      <ActualSubscriptionCancelDialog />
      <TransactionCrud
        accountId={user?.account.id}
        type={transactionCrudOpen.type}
        open={transactionCrudOpen.show}
        edit={transactionCrudOpen.edit}
        onClose={() => setTransactionCrudOpen({ show: false, type: "", edit: undefined })}
        onConfirm={handleTransactionConfirm}
        title={transactionCrudOpen.type}
      />
      <PortionCustomize
        open={portionCustomizeOpen}
        close={portionCustomizeClose}
        customize={{
          request: selection?.customPortionRequest,
          current: selection?.portion,
          portions: selection?.meal?.portions,
          userId: id,
          selectionId: selection?.id,
          mealId: selection?.meal?.id,
          readOnly: selectionMode.state,
        }}
      />
      <DialogReview />
      <Fragment>
        <Dialog
          open={stopPanelOpen.visibility}
          TransitionComponent={Transition}
          onClose={handleStopPanelClose}
          aria-describedby="alert-dialog-slide-description"
          sx={{
            "& .MuiPaper-root": {
              borderRadius: "16px",
              boxShadow: "0px 12px 24px 0px rgba(0, 0, 0, 0.15)",
              padding: "24px",
              width: "420px",
              maxWidth: "420px",
            },
            "& .MuiDialogTitle-root": {
              padding: "0 16px 16px 0",
            },
            "& .MuiDialogContent-root,& .MuiDialogActions-root": {
              padding: 0,
            },
          }}
        >
          <DialogTitle
            sx={{
              color: "text.dark",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "20px",
            }}
          >
            {stopPanelOpen.mode === "stop" ? "Pause Plan" : "Edit Schedule"}
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleStopPanelClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "text.fontLight2",
            }}
          >
            <CrossIcon />
          </IconButton>
          <DialogContent>
            <Box sx={{ width: "100%" }}>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs value={tabModeValue} onChange={handleTabChange}>
                  <Tab sx={{ textTransform: "capitalize" }} label="Add Specific Dates" />
                  <Tab sx={{ textTransform: "capitalize" }} label="Add Range" />
                </Tabs>
              </Box>
              <CustomTabPanel value={tabModeValue} index={0}>
                <DateCalendar
                  value={calendarDate}
                  // dayOfWeekFormatter={(_day: string, date: Moment) => moment(date).format("dd")}
                  showDaysOutsideCurrentMonth
                  views={["day"]}
                  minDate={calendarMinDate}
                  maxDate={calendarMaxDate}
                  onChange={handleCalendarDateChange}
                  sx={{
                    "& .MuiPickersDay-dayOutsideMonth": {
                      opacity: "0.25",
                    },
                  }}
                  slots={{
                    day: CustomDaySubscription,
                  }}
                  slotProps={{
                    day: {
                      daysinfo: selectedPauseDays,
                    } as any,
                  }}
                />
              </CustomTabPanel>
              <CustomTabPanel value={tabModeValue} index={1}>
                <Box sx={{ marginTop: "16px", display: "flex", gap: "4px", alignItems: "flex-start" }}>
                  <DatePicker
                    slotProps={{
                      actionBar: {
                        actions: ["clear"],
                      },
                      textField: {
                        helperText: !checkIf(calendarError?.startDate)
                          ? null
                          : translateError(String(calendarError?.startDate)),
                      },
                    }}
                    label="Start Date"
                    minDate={calendarMinDate}
                    shouldDisableDate={(day) =>
                      Boolean(
                        selectedPauseDays.find(
                          (pausedDay) =>
                            pausedDay.startDate === pausedDay.endDate &&
                            day.format(defaultFormat) === pausedDay.startDate
                        )
                      )
                    }
                    format={displayFormat}
                    value={datePickerValue.startDate}
                    onChange={(newValue: Moment | null) =>
                      setDatePickerValue((state) => ({ ...state, startDate: newValue }))
                    }
                    onError={(newError) => setCalendarError((state) => ({ ...state, startDate: newError }))}
                  />
                  <DatePicker
                    slotProps={{
                      actionBar: {
                        actions: ["clear"],
                      },
                      textField: {
                        helperText: !checkIf(calendarError?.endDate)
                          ? null
                          : translateError(String(calendarError?.endDate)),
                      },
                    }}
                    label="End Date"
                    minDate={calendarMinDate}
                    format={displayFormat}
                    shouldDisableDate={(day) =>
                      moment(day).isBefore(datePickerValue.startDate) ||
                      Boolean(
                        selectedPauseDays.find(
                          (pausedDay) =>
                            pausedDay.startDate === pausedDay.endDate &&
                            day.format(defaultFormat) === pausedDay.startDate
                        )
                      )
                    }
                    value={datePickerValue.endDate}
                    onChange={(newValue: Moment | null) =>
                      setDatePickerValue((state) => ({ ...state, endDate: newValue }))
                    }
                    onError={(newError) => setCalendarError((state) => ({ ...state, endDate: newError }))}
                  />
                  <IconButton color="primary" sx={{ marginTop: "6px" }} onClick={addRange}>
                    <PlusIcon />
                  </IconButton>
                </Box>
              </CustomTabPanel>
              {additionLimitWarning && (
                <Box sx={{ display: "inline-flex", alignItems: "flex-start", gap: "4px", marginBottom: "4px" }}>
                  <InfoIcon color={theme.palette.text.blue} />
                  <Typography sx={{ color: "text.dark2", fontSize: "12px", fontWeight: 500, lineHeight: "150%" }}>
                    You may save 10 new entries at once. Please save the selected dates, and proceed with another
                    request.
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: "inline-flex", alignItems: "flex-start", gap: "4px", flexWrap: "wrap" }}>
                {selectedPauseDays?.map((day, key) => {
                  const text =
                    day.startDate === day.endDate
                      ? moment(day.startDate).format(displayFormat)
                      : moment(day.startDate).format(displayFormat) +
                        "-" +
                        (day.endDate ? moment(day.endDate).format(displayFormat) : "Continuous");
                  return (
                    <Tooltip
                      key={key}
                      title={`${day.disabled ? "This can't be deleted as it is past date" : ""}`}
                      placement="top"
                      arrow
                    >
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                          borderRadius: "4px",
                          backgroundColor: "text.tableColor",
                          padding: "6px 8px",
                          height: "30px",
                        }}
                      >
                        <Typography
                          sx={{
                            color: "text.dark",
                            fontSize: "12px",
                            whiteSpace: "nowrap",
                          }}
                          component={"span"}
                        >
                          {text}
                        </Typography>
                        <Box
                          sx={{
                            pointerEvents: `${day.disabled ? "none" : "auto"}`,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "text.fontLight2",
                            cursor: "pointer",
                            "&:hover": {
                              color: "text.dark",
                            },
                          }}
                        >
                          <CloseIconCircle onClick={() => deletePausedItem(day)} />
                        </Box>
                      </Box>
                    </Tooltip>
                  );
                })}
                {selectedPauseDays.length === 0 && (
                  <Box sx={{ display: "inline-flex", alignItems: "flex-start", gap: "4px" }}>
                    <InfoIcon color={theme.palette.text.blue} />
                    <Typography sx={{ color: "text.dark2", fontSize: "12px", fontWeight: 500, lineHeight: "150%" }}>
                      {subscriptionInfoText}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            {stopPanelOpen.mode === "stop" ? (
              <LoadingButton
                fullWidth
                loading={loading}
                color="warning"
                disabled={!selectedPauseDays.length}
                variant="contained"
                sx={{ display: "flex", flexDirection: "column", marginTop: "16px" }}
                onClick={stopPlan}
              >
                Save Dates
              </LoadingButton>
            ) : (
              <LoadingButton
                fullWidth
                loading={loading}
                color="warning"
                variant="contained"
                sx={{ display: "flex", flexDirection: "column", marginTop: "16px" }}
                onClick={editPlan}
              >
                Save Dates
              </LoadingButton>
            )}
          </DialogActions>
        </Dialog>
      </Fragment>
      <Fragment>
        <Dialog
          open={resumePanelOpen}
          TransitionComponent={Transition}
          onClose={handleResumePanelClose}
          aria-describedby="alert-dialog-slide-description"
          sx={{
            "& .MuiPaper-root": {
              borderRadius: "16px",
              boxShadow: "0px 12px 24px 0px rgba(0, 0, 0, 0.15)",
              padding: "24px",
              width: "360px",
              maxWidth: "360px",
            },
            "& .MuiDialogTitle-root": {
              padding: "0 16px 16px 0",
            },
            "& .MuiDialogContent-root,& .MuiDialogActions-root": {
              padding: 0,
            },
          }}
        >
          <DialogTitle
            sx={{
              color: "text.dark",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "20px",
            }}
          >
            Delete Pause Period
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleResumePanelClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "text.fontLight2",
            }}
          >
            <CrossIcon />
          </IconButton>
          <DialogContent>
            <Typography sx={{ color: "text.dark2", fontSize: "14px", fontWeight: 500, lineHeight: "157%" }}>
              Are you sure you want to delete this pause period ?
            </Typography>
          </DialogContent>
          <DialogActions>
            <LoadingButton
              fullWidth
              loading={loading}
              color="error"
              variant="contained"
              sx={{ display: "flex", flexDirection: "column", marginTop: "16px" }}
              onClick={resumePlan}
            >
              Delete
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </Fragment>
      <UserCrud
        create
        open={multipleUserDialog.open}
        title="Create User into Membership"
        membershipInfo={{
          id: user?.membershipId,
          phoneNumber: user?.phoneNumber,
        }}
        onClose={() => {
          setMultipleUserDialog({ open: false });
          setRetriggerMultipleUserReq(generateRandom());
        }}
        onConfirm={() => {}}
      />
      <APIProvider apiKey={String(googleMapsApiKey)}>
        <Dialog
          open={mapDisplay}
          TransitionComponent={Transition}
          onClose={() => {}}
          aria-describedby="alert-dialog-slide-description"
          sx={{
            "& .MuiPaper-root": {
              borderRadius: "16px",
              boxShadow: "0px 12px 24px 0px rgba(0, 0, 0, 0.15)",
              // padding: "24px",
              width: "564px",
              maxWidth: "564px",
            },
            "& .MuiDialogTitle-root": {
              padding: "0 16px 16px 0",
            },
            "& .MuiDialogContent-root,& .MuiDialogActions-root": {
              padding: 0,
            },
          }}
        >
          <IconButton
            aria-label="close"
            onClick={() => {}}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "#fff",
            }}
          >
            <CrossIcon />
          </IconButton>
          <DialogContent>
            <AddressMap onClose={ChooseAddressFromMap} selectedCoordinates={selectedCoordinates} />
          </DialogContent>
        </Dialog>
      </APIProvider>
    </Box>
  );
}
